import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useStableCallback } from "./components/useStableCallback";
import {
  collection,
  deleteDoc,
  doc,
  documentId,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  getDocs,
  where,
} from "firebase/firestore";
import {
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInAnonymously,
  signOut,
} from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "./firebase";
import { useProjectCollection } from "./hooks/useProjectCollection";

import HomeSection from "./components/HomeSection";
import { buildGccPhasedScheduleSummary } from "./components/GccDueCompletionPhasedSection";
import CashflowSection from "./components/CashflowSection";
import CertificatesSection from "./components/CertificatesSection";
import RFISection from "./components/RFISection";
import InstructionsSection from "./components/InstructionSection";
import VariationsSection from "./components/VariationSection";
import ProgrammeSection from "./components/ProgrammeSection";
import DueCompletionPanel from "./components/DueCompletionPanel";
import BOQSection from "./components/BOQSection";
import FinancialDashboardSection from "./components/FinancialDashboardSection";
import Contractsection from "./components/Contractsection";
import EnvironmentalAuditSection from "./components/EnvironmentalAuditSection";
import HealthSafetyAuditSection from "./components/HealthSafetyAuditSection";
import DiariesSection from "./components/DiariesSection";
import VisualProgressTracker from "./components/VisualProgressTracker";
import AuditTrailSection from "./components/AuditTrailSection";
import NCRSection, { emptyNcrForm } from "./components/NCRSection";
import ClaimsSection, { emptyClaimForm } from "./components/ClaimsSection";
import SOQImportSection from "./components/SOQImportSection";
import SOQRegisterSection from "./components/SOQRegisterSection";
import CPASection, {
  buildFidicCpaByCertificateNo,
  calculateFidicCpaTotals,
  calculateLatestCpaForContract,
  createDefaultFidicCpaState,
  createEmptyCpaColumn,
} from "./components/CPA";
import { parseIndexMonth, fetchStatssaIndices, mapResultToIndexValues, STATSSA_SERIES_KEYS, MATERIAL_SERIES_DEFAULT_KEY } from "./utils/statssaIndices";

import ROEsection, {
  getRoeThisClaimForCertificate,
  getRoeThisClaimForMonth,
  getRoeCumulativeUpToCertificate,
} from "./components/ROEsection";

import ClaudeSection from "./components/ClaudeSection";
import ProjectTracker from "./components/ProjectTracker";
import RiskSection from "./components/RiskSection";
import UserManagement from "./components/UserManagement";
import ClientManagement from "./components/ClientManagement";
import ProjectAdminSection from "./components/ProjectAdminSection";
import QualityTestsSection from "./components/QualityTestsSection";
import "./components/theme.css";
import "./mobile-redesign.css";
import MobileApp from "./components/MobileApp";
import { COPYRIGHT, RESTRICTION, LICENCE_SUMMARY } from "./legal";
import { BUILD_INFO } from "./buildInfo";
import ContractMeetingsSection from "./components/ContractMeetingsSection";
import MaterialsOnSiteSection from "./components/MaterialsOnSiteSection";
import DefectsSection from "./components/DefectsSection";
import DrawingRegisterSection from "./components/DrawingRegisterSection";
import CompletionDefectsSection from "./components/CompletionDefectsSection";
import TimeBarSection from "./components/TimeBarSection";
import FinalAccountSection from "./components/FinalAccountSection";
import TemplatesSection from "./components/TemplatesSection";
import { appendAudit } from "./utils/audit";
import {
  addContractDaysAfter,
  calculateEstimatedEndDate,
  normaliseBuildersBreaks,
  normaliseDaysToSkip,
} from "./utils/contractCalendar";
import { nextRfiNumber } from "./utils/rfiNumbering";
import { getInstructionConfig, suggestNextSiNumber } from "./components/InstructionFormatPanel";
import { APP_STYLES } from "./appStyles";
import SectionNavigator from "./components/SectionNavigator";

// Where the public marketing site lives — used by the in-app demo banner CTAs.
const MARKETING_URL = "https://www.soloforce.co.za";

// APP_STYLES design tokens now live in ./appStyles.js (imported above).


// SectionNavigator + SECTION_GROUPS + NavButton now live in ./components/SectionNavigator.js




function numberOrZero(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function normalizeBoqValue(value) {
  return String(value || "").trim().toLowerCase();
}

function buildPossibleBoqKeys(...values) {
  return values.map((value) => normalizeBoqValue(value)).filter(Boolean);
}

function isApprovedVariation(item) {
  return String(item?.status || "").trim().toLowerCase() === "approved";
}

function isOmissionVariation(item) {
  const descriptor = [
    item?.type,
    item?.voType,
    item?.variationType,
    item?.description,
    item?.title,
    item?.detail,
    item?.scope,
    item?.reason,
    item?.costCategory,
  ]
    .join(" ")
    .toLowerCase();

  return descriptor.includes("omission") || descriptor.includes("omit") || numberOrZero(item?.omittedWorkAmount) > 0;
}

function getVariationOmissionAmount(item) {
  const directOmission = Math.abs(
    numberOrZero(
      item?.omittedWorkAmount ??
        item?.omissionValue ??
        item?.omittedValue ??
        item?.omitValue ??
        item?.netOmissionAmount ??
        item?.omissionAmount ??
        item?.omittedWorkValue ??
        0
    )
  );


if (directOmission > 0) return directOmission;

  const derivedNet =

    numberOrZero(item?.additionalWorkAmount ?? 0) +

    numberOrZero(item?.existingRatesAmount ?? 0) -

    numberOrZero(item?.omittedWorkAmount ?? 0);

  if (derivedNet < 0) return Math.abs(derivedNet);

  return 0;

}

function calculateCpaColumnForApp(column, previousColumn) {
  const labourWeight = 0.3;
  const plantWeight = 0.3;
  const materialWeight = 0.3;
  const fuelWeight = 0.1;
  const nonAdjustablePortion = 0.1;
  const defaultBaseIndices = {
    labourIndex: 90.8213,
    plantIndex: 91.6163,
    materialIndex: 88.6819,
    fuelIndex: 116.9507,
  };
  const defaultBaseSteel = 9299;
  const baseIndexSets =
    Array.isArray(column?.baseIndexSets) && column.baseIndexSets.length
      ? column.baseIndexSets
      : [{ id: "base-set-1", label: "Base Set 1", baseIndices: defaultBaseIndices }];
  const selectedBaseSet =
    baseIndexSets.find((set) => String(set.id) === String(column?.baseIndexSetId)) ||
    baseIndexSets[0];
  const baseLabourIndex =
    numberOrZero(selectedBaseSet?.baseIndices?.labourIndex) || defaultBaseIndices.labourIndex;
  const basePlantIndex =
    numberOrZero(selectedBaseSet?.baseIndices?.plantIndex) || defaultBaseIndices.plantIndex;
  const baseMaterialIndex =
    numberOrZero(selectedBaseSet?.baseIndices?.materialIndex) || defaultBaseIndices.materialIndex;
  const baseFuelIndex =
    numberOrZero(selectedBaseSet?.baseIndices?.fuelIndex) || defaultBaseIndices.fuelIndex;

  const labourIndex = numberOrZero(column?.labourIndex);
  const plantIndex = numberOrZero(column?.plantIndex);
  const materialIndex = numberOrZero(column?.materialIndex);
  const fuelIndex = numberOrZero(column?.fuelIndex);

  const cpaFactor =
    Math.round(
      ((1 - nonAdjustablePortion) *
        (((labourWeight * labourIndex) / baseLabourIndex) +
          ((plantWeight * plantIndex) / basePlantIndex) +
          ((materialWeight * materialIndex) / baseMaterialIndex) +
          ((fuelWeight * fuelIndex) / baseFuelIndex) -
          1)) *
        10000
    ) / 10000;

  const totalScheduledItems = numberOrZero(column?.totalScheduledItems);
  const addMos = numberOrZero(column?.addMos);
  const addVariations = numberOrZero(column?.addVariations);
  const subtractNonEscalatable = numberOrZero(column?.subtractNonEscalatable);
  const subtractDayworks = numberOrZero(column?.subtractDayworks);

  const subtotal =
    totalScheduledItems + addMos + addVariations - subtractNonEscalatable - subtractDayworks;

  const previousSubtotal = numberOrZero(previousColumn?.subtotal);
  const subtractPreviousTotal = subtotal - previousSubtotal;
  const cpaGeneral = subtractPreviousTotal * cpaFactor;

  const baseSteel = numberOrZero(column?.baseSteel || defaultBaseSteel);
  const steelRate = numberOrZero(column?.steelRate);
  const steelVolume = numberOrZero(column?.steelVolume);
  const specialMaterialVariation = (steelRate - baseSteel) * steelVolume;

  const cpaTotal = cpaGeneral + specialMaterialVariation;
  const previousCumulativeCpaTotal = numberOrZero(previousColumn?.totalCumulativeCpa);
  const totalCumulativeCpa = previousCumulativeCpaTotal + cpaTotal;
  const certifiedValueIncludingCpa = cpaTotal + subtractPreviousTotal;

  return {
    baseIndexSetId: selectedBaseSet?.id || "base-set-1",
    baseIndexSetLabel: selectedBaseSet?.label || "Base Set 1",
    baseIndexRevisedMonth: selectedBaseSet?.revisedMonth || "",
    baseLabourIndex,
    basePlantIndex,
    baseMaterialIndex,
    baseFuelIndex,
    cpaFactor,
    subtotal,
    subtractPreviousTotal,
    cpaGeneral,
    specialMaterialVariation,
    cpaTotal,
    previousCumulativeCpaTotal,
    totalCumulativeCpa,
    certifiedValueIncludingCpa,
  };
}

function toMonthKey(dateValue) {
  if (!dateValue) return "";
  const value = String(dateValue).trim();
  if (/^\d{4}-\d{2}$/.test(value)) return value;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value.slice(0, 7);
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
}

function buildMonthRange(startDate, endDate) {
  const startKey = toMonthKey(startDate);
  const endKey = toMonthKey(endDate);

  if (!startKey && !endKey) {
    const today = new Date();
    const fallback = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
    return [fallback];
  }

  const safeStart = startKey || endKey;
  const safeEnd = endKey || startKey;

  const [startYear, startMonth] = safeStart.split("-").map(Number);
  const [endYear, endMonth] = safeEnd.split("-").map(Number);

  const start = new Date(startYear, startMonth - 1, 1);
  const end = new Date(endYear, endMonth - 1, 1);

  if (start > end) {
    return [safeStart];
  }

  const months = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    months.push(`${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}`);
    cursor.setMonth(cursor.getMonth() + 1);
  }

  return months.length ? months : [safeStart];
}

function formatMonthLabel(monthKey) {
  if (!monthKey || !/^\d{4}-\d{2}$/.test(String(monthKey))) return String(monthKey || "");
  const [year, month] = String(monthKey).split("-").map(Number);
  const date = new Date(year, month - 1, 1);
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
  });
}

function buildBlockMap(monthRange, existing = {}) {
  const keys = Array.from(
    new Set([
      ...(monthRange || []),
      ...Object.keys(existing || {}),
    ])
  ).filter(Boolean);

  return keys.reduce((acc, monthKey) => {
    const existingMonth = existing?.[monthKey] || {};

    acc[monthKey] = {
      ...existingMonth,
      scheduledWorks: existingMonth.scheduledWorks ?? "",
      cpa: existingMonth.cpa ?? "",
      extraWork:
        existingMonth.extraWork ??
        existingMonth.vos ??
        existingMonth.variations ??
        "",
      total: existingMonth.total ?? existingMonth.actualMonthlyTotal ?? "",
      actualMonthlyTotal: existingMonth.actualMonthlyTotal ?? existingMonth.total ?? "",
    };
    return acc;
  }, {});
}

function pickFirstNumber(...values) {
  const match = values.find((value) => {
    if (value === undefined || value === null) return false;
    if (String(value).trim() === "") return false;
    return Number.isFinite(Number(value));
  });

  return match === undefined ? null : numberOrZero(match);
}

function getCertificateCpaThisPeriod(item, fallbackValue = 0) {
  const directCpa = pickFirstNumber(
    item?.cpaThisCertificate,
    item?.cpaCertifiedValue,
    item?.cpaCertifiedThisPeriod,
    item?.certifiedCpa,
    item?.certifiedCPA,
    item?.actualCpa,
    item?.cpaAmount,
    item?.cpaThisMonth,
    item?.monthlyCpa,
    item?.contractPriceAdjustment,
    item?.priceAdjustment,
    item?.escalation
  );

  return directCpa === null ? numberOrZero(fallbackValue) : directCpa;
}

// --- Begin: Extra Work/Variation helpers ---

function sumCertificateExtraWorkItems(item) {
  const possibleLists = [
    item?.extraWorkItems,
    item?.selectedExtraWorkItems,
    item?.certificateExtraWorkItems,
    item?.approvedVoItems,
    item?.approvedVos,
    item?.voItems,
    item?.vos,
    item?.selectedVos,
    item?.selectedVOs,
    item?.variationItems,
    item?.selectedVariationItems,
    item?.variationsIncluded,
    item?.includedVariations,
    item?.selectedExtraWorks,
    item?.extraWorksIncluded,
  ];

  return possibleLists.reduce((total, list) => {
    if (!Array.isArray(list)) return total;

    return total + list.reduce((sum, row) => {
      const quantity = numberOrZero(
        row?.quantity ??
          row?.qty ??
          row?.approvedQty ??
          row?.certifiedQty ??
          row?.thisCertificateQty ??
          0
      );
      const rate = numberOrZero(
        row?.rate ??
          row?.approvedRate ??
          row?.unitRate ??
          row?.tenderRate ??
          0
      );
      const quantityRateAmount = quantity && rate ? quantity * rate : 0;

      return sum + numberOrZero(
        row?.amountThisCertificate ??
          row?.thisCertificateAmount ??
          row?.certifiedThisPeriod ??
          row?.amountCertifiedThisPeriod ??
          row?.monthAmount ??
          row?.totalAmount ??
          row?.amount ??
          row?.value ??
          row?.approvedValue ??
          row?.approvedAmount ??
          row?.lineTotal ??
          quantityRateAmount ??
          0
      );
    }, 0);
  }, 0);
}







function getCertificateExtraWorkDirectValue(item) {
  const directExtraWork = pickFirstNumber(
    item?.extraWorkThisCertificate,
    item?.extraWorksThisCertificate,
    item?.extraWork,
    item?.extraWorks,
    item?.voThisCertificate,
    item?.vosThisCertificate,
    item?.variationThisCertificate,
    item?.variationsThisCertificate,
    item?.approvedVoThisCertificate,
    item?.approvedVosThisCertificate,
    item?.approvedVariationThisCertificate,
    item?.approvedVariationsThisCertificate,
    item?.extraWorkAmount,
    item?.voAmount,
    item?.variationAmount
  );

const arrayExtraWork = sumCertificateExtraWorkItems(item);
if (arrayExtraWork) return arrayExtraWork;


  return directExtraWork === null ? null : directExtraWork;
}

function getVariationCertificateReference(item) {
  return String(
    item?.paymentCertificateRef ||
      item?.paymentCertificateNo ||
      item?.paymentCertificate ||
      item?.certificateNo ||
      item?.certNo ||
      item?.certificateRef ||
      item?.includedInCertificateNo ||
      item?.includedInCertificate ||
      item?.includedInPc ||
      item?.includedInPC ||
      item?.pcNo ||
      item?.paymentCertNo ||
      ""
  ).trim();
}

function getVariationCertifiedAmount(item) {
  const grossVoAmount =
    numberOrZero(item?.paymentCertificateVoAmount) ||
    numberOrZero(item?.grossVoAmount) ||
    numberOrZero(item?.additionalWorkAmount) + numberOrZero(item?.existingRatesAmount);

  return numberOrZero(
    item?.amountCertifiedThisPeriod ??
      item?.certifiedThisPeriod ??
      item?.certificateAmount ??
      item?.amountIncludedInCertificate ??
      item?.includedAmount ??
      item?.thisCertificateAmount ??
      item?.amountThisCertificate ??
      item?.monthAmount ??
      item?.paymentCertificateVoAmount ??
      item?.grossVoAmount ??
      item?.approvedValue ??
      item?.approvedAmount ??
      grossVoAmount ??
      0
  );
}

function getCertificateExtraWorkThisPeriod(item, sourceVariations = []) {
  const directExtraWork = getCertificateExtraWorkDirectValue(item);
  if (directExtraWork !== null) return directExtraWork;

  const certificateNo = String(item?.certificateNo || item?.id || "").trim();
  if (!certificateNo) return 0;

  return (sourceVariations || []).reduce((sum, variation) => {
    const variationCertificateRef = getVariationCertificateReference(variation);
    if (!variationCertificateRef) return sum;

    const matchesCertificate = String(variationCertificateRef).localeCompare(certificateNo, undefined, {
      numeric: true,
      sensitivity: "base",
    }) === 0;

    if (!matchesCertificate) return sum;

    return sum + getVariationCertifiedAmount(variation);
  }, 0);
}

function getCertificateSubtotalAForCashflow(item) {
  return numberOrZero(
    item?.effectiveSubtotalA ??
      item?.liveSubtotalA ??
      item?.displayedSubtotalA ??
      item?.recalculatedSubtotalA ??
      item?.subtotalA ??
      item?.totalSubtotalA ??
      0
  );
}

function getCertificateSubtotalBForCashflow(item, subtotalAValue = 0, cpaCumulativeValue = 0) {
  const directSubtotalB = pickFirstNumber(
    item?.effectiveSubtotalB,
    item?.liveSubtotalB,
    item?.displayedSubtotalB,
    item?.recalculatedSubtotalB,
    item?.subtotalB,
    item?.totalSubtotalB
  );

  if (directSubtotalB !== null) return directSubtotalB;

  return numberOrZero(subtotalAValue) + numberOrZero(cpaCumulativeValue);
}

function getCertificateCpaCumulativeForCashflow(item, subtotalAValue = 0) {
  const directCumulative = pickFirstNumber(
    item?.currentCpaCumulative,
    item?.liveCpaCumulative,
    item?.cpaCumulative,
    item?.lockedCpaCumulative,
    item?.cpaTotalCumulative,
    item?.cumulativeCpa,
    item?.cumulativeCPA
  );

  if (directCumulative !== null) return directCumulative;

  const subtotalB = pickFirstNumber(
    item?.effectiveSubtotalB,
    item?.liveSubtotalB,
    item?.displayedSubtotalB,
    item?.recalculatedSubtotalB,
    item?.subtotalB,
    item?.totalSubtotalB
  );

  if (subtotalB !== null) return Math.max(0, subtotalB - numberOrZero(subtotalAValue));

  return 0;
}

function getCertificatePaidAmountForCashflow(item) {
  const paidAmount = pickFirstNumber(
    item?.paidAmount,
    item?.amountPaid,
    item?.paymentAmount,
    item?.amountPaidToDate,
    item?.paidToDate,
    item?.certifiedPaidAmount,
    item?.certificatePaidAmount,
    item?.grossAmountDue,
    item?.grossAmountDueForRow,
    item?.amountNowDue
  );

  return paidAmount === null ? 0 : paidAmount;
}

function getCertificateCpaInitialForCashflow(item, previousCpaCumulative = 0) {
  // The cashflow SUMS this value across every certificate/month, so it must be the
  // per-certificate CPA MOVEMENT (this period) — NOT the cumulative-to-date figure.
  // Summing cumulative values caused an ~8x overshoot (e.g. R8.41m read as R68.5m).
  const cpaThisPeriod = pickFirstNumber(
    item?.currentCpaThisCertificate,
    item?.liveCpaThisCertificate,
    item?.cpaThisCertificate,
    item?.cpaThisPeriod,
    item?.cpaCertifiedThisPeriod
  );

  if (cpaThisPeriod !== null) return numberOrZero(cpaThisPeriod);

  // Legacy fallback: derive the movement from the cumulative-to-date figure.
  const cumulative = getCertificateCpaCumulativeForCashflow(item);
  return numberOrZero(cumulative) - numberOrZero(previousCpaCumulative);
}

function sumCertificateClaimedItemsForCashflow(item) {
  const list = Array.isArray(item?.claimedItems) ? item.claimedItems : [];

  return list.reduce((sum, row) => {
    const amount = pickFirstNumber(
      row?.amountThisMonth,
      row?.amountThisCertificate,
      row?.thisCertificateAmount,
      row?.amountCertifiedThisPeriod,
      row?.monthAmount,
      row?.amount,
      row?.lineTotal
    );

    if (amount !== null) return sum + amount;

    return sum + numberOrZero(row?.thisMonthQty) * numberOrZero(row?.rate);
  }, 0);
}

function getCertificateScheduledWorksAForCashflow(item, fallbackValue = 0) {
  const directScheduledWorks = pickFirstNumber(
    item?.effectiveScheduledWorks,
    item?.scheduledWorks,
    item?.scheduledWorksA,
    item?.actualScheduledWorksA,
    item?.actualScheduledA,
    item?.scheduledWorksThisCertificate,
    item?.scheduledWorksThisPeriod,
    item?.workThisCertificate,
    item?.worksThisCertificate,
    item?.amountThisCertificateExCpa,
    item?.amountThisCertificateExCPA
  );

  return directScheduledWorks === null ? numberOrZero(fallbackValue) : directScheduledWorks;
}
// --- End: Extra Work/Variation helpers ---

function getLatestCpaIndexStatusForApp(contractType, computedCpaColumns = [], fidicTables = {}) {
  const safeContractType = String(contractType || "GCC").toUpperCase();

  if (safeContractType === "FIDIC") {
    const statuses = [];
    Object.values(fidicTables || {}).forEach((table) => {
      (table?.rows || []).forEach((row) => {
        if (row?.indexStatus) statuses.push(String(row.indexStatus).toUpperCase());
      });
    });

    if (statuses.length === 0) return "";
    return statuses.every((status) => status === "F") ? "F" : "P";
  }

  const linkedColumns = [...(computedCpaColumns || [])].filter(
    (column) => column?.certificateNo || column?.certificateMonth
  );
  const latestColumn = linkedColumns[linkedColumns.length - 1] || computedCpaColumns?.[computedCpaColumns.length - 1];
  return String(latestColumn?.indexStatus || "").toUpperCase();
}

function getCpaIndexStatusForCertificateForApp(contractType, certificateNo, certificateMonth, computedCpaColumns = [], fidicTables = {}) {
  const safeContractType = String(contractType || "GCC").toUpperCase();
  const safeCertificateNo = String(certificateNo || "").trim();
  const safeCertificateMonth = String(certificateMonth || "").trim();

  if (safeContractType === "FIDIC") {
    const statuses = [];
    Object.values(fidicTables || {}).forEach((table) => {
      (table?.rows || []).forEach((row) => {
        if (String(row?.certificateNo || "").trim() === safeCertificateNo && row?.indexStatus) {
          statuses.push(String(row.indexStatus).toUpperCase());
        }
      });
    });

    if (statuses.length === 0) return getLatestCpaIndexStatusForApp(contractType, computedCpaColumns, fidicTables);
    return statuses.every((status) => status === "F") ? "F" : "P";
  }

  const matchingColumn = (computedCpaColumns || []).find(
    (item) =>
      String(item.certificateNo || "") === safeCertificateNo ||
      String(item.certificateMonth || "") === safeCertificateMonth
  );

  return String(matchingColumn?.indexStatus || getLatestCpaIndexStatusForApp(contractType, computedCpaColumns, fidicTables) || "").toUpperCase();
}

function buildFidicCpaByCertificateNoFromTables(fidicTables) {
  const totals = {};
  const fidicTotals = calculateFidicCpaTotals(fidicTables);

  Object.values(fidicTotals.computedTables || {}).forEach((table) => {
    if (table.enabled === false) return;

    (table.rows || []).forEach((row) => {
      const certificateNo = String(row.certificateNo || "").trim();
      if (!certificateNo) return;

      if (!totals[certificateNo]) {
        totals[certificateNo] = {
          cpaCertifiedValue: 0,
          cpaThisCertificate: 0,
          cpaCumulative: 0,
        };
      }

      const escalation = numberOrZero(row.computed?.escalation);
      totals[certificateNo].cpaCertifiedValue += escalation;
      totals[certificateNo].cpaThisCertificate += escalation;
      totals[certificateNo].cpaCumulative += escalation;
    });
  });

  return totals;
}

function makeProjectForm(projectId = "") {
  return {
    id: projectId,
    name: "",
    client: "",
    department: "",
    contractNumber: "",
    contractDescription: "",
    contractor: "",
    contractType: "GCC",
    cpaType: "GCC",
    status: "Active",
    progress: 0,
    contractValue: 0,
    revisedValue: 0,
    scheduledWorks: 0,
    startDate: "",
    contractDurationDays: "",
    contractDurationLabel: "",
    contractDurationValue: "",
    contractDurationUnit: "",
    daysToSkip: normaliseDaysToSkip(),
    buildersBreakCount: 0,
    buildersBreaks: [],
    estimatedEndDate: "",
    revisedEndDate: "",
    completionDate: "",
  };
}

function normaliseCpaType(value) {
  return String(value || "GCC").toUpperCase() === "FIDIC" ? "FIDIC" : "GCC";
}

function makeRfiForm(projectId = "") {
  return {
    rfiNo: "",
    dateSubmitted: "",
    originator: "",
    contractor: "",
    discipline: "",
    subject: "",
    reason: "",
    reference: "",
    requiredResponseDate: "",
    dateResponded: "",
    responseSummary: "",
    status: "Open",
    impact: "",
    relatedVariationId: "",
    responseCreatesVariation: "No",
    notes: "",
    projectId,
  };
}

function makeInstructionForm(projectId = "") {
  return {
    siNumber: "",
    projectNumber: "",
    contractNumber: "",
    contractDescription: "",
    contractor: "",
    issuedBy: "",
    subject: "",
    instructionText: "",
    drawingRevisionRef: "",
    applicablePayItems: "",
    asBuiltImplications: "",
    financialImplications: "No",
    valuationBasis: "",
    employersAgentRepresentative: "",
    instructionDate: "",
    instructionTime: "",
    contractorRemarks: "",
    receivedBy: "",
    receivedDate: "",
    receivedTime: "",
    distribution: "",
    status: "",
    relatedRfiId: "",
    relatedVariationId: "",
    projectId,
customFields: {},
  };
}

function makeVariationForm(projectId = "") {
  return {
    projectId,
    variationNo: "",
    requestDate: "",
    voDescription: "",
    estimatedAdditionalDays: "",
    motivation: "",
    reason: "Additional Work",
    requestedBy: "Employer",
    fundingSource: "Contingency",
    employer: "",
    engineerRef: "",
    contractorRef: "",
    contractor: "",
    title: "",
    description: "",
    instructionRef: "",
    rfiRef: "",
    boqSection: "",
    drawingRef: "",
    specificationRef: "",
    clauseRef: "",
    initiatedBy: "Engineer",
    dateInstructed: "",
    dateSubmitted: "",
    dateAssessed: "",
    dateApproved: "",
    costCategory: "addition",
    estimatedValue: "",
    quotedValue: "",
    assessedValue: "",
    approvedValue: "",
    status: "Draft",
    eotClaimedDays: "",
    eotAssessedDays: "",
    eotApprovedDays: "",
    criticalPathImpact: "No",
    programmeRef: "",
    paymentCertificateRef: "",
    amountPreviouslyCertified: "",
    amountCertifiedThisPeriod: "",
    contractContingenciesAmount: "",
    submittedVariationOrdersAmount: "",
    totalVariationOrdersAmount: "",
    originalDueCompletionDate: "",
    currentDueCompletionDate: "",
    projectedDueCompletionDate: "",
    additionalWorkAmount: "",

existingRatesAmount: "",

grossVoAmount: "",

paymentCertificateVoAmount: "",

omittedWorkAmount: "",

netVoAmount: "",

notes: "",
  };
}

function makeCertificateForm(projectId = "") {
  return {
    certificateNo: "",
    certificateDate: "",
    certificateMonth: "",
    subtotalA: "",
    subtotalB: "",
    retentionOverride: "",
    cpaCumulative: "",
    cpaCertifiedValue: "",
    cpaThisCertificate: "",
    lockedCpaCumulative: "",
    lockedCpaThisCertificate: "",
    lockedCpaCertifiedValue: "",
    lockedCpaSource: "",
    lockedCpaLockedAt: "",
    lockedCpaIndexStatus: "",
    lockedCpaCertificateNo: "",
    projectId,
  };
}

function makeDiaryForm(projectId = "") {
  return {
    diaryNo: "",
    date: "",
    weather: "",
    workSection: "",
    linkedProgrammeActivityId: "",
    linkedProgrammeActivityCode: "",
    linkedProgrammeActivityName: "",
    programmeActivityStatus: "",
    programmeActivityNote: "",

    // Visual Progress Tracker fields (Version 1)
    progressTrackingEnabled: "No",
    progressAssetId: "",
    progressAssetName: "",
    progressAssetType: "pipeline",
    progressActivity: "pipe_laying",
    progressChainageStart: "",
    progressChainageEnd: "",
    progressStatus: "completed",
    progressNotes: "",

    worksPerformed: "",
    generalComments: "",
    contractorSignName: "",
    contractorSigned: "No",
    engineerSignName: "",
    engineerSigned: "No",
    isLocked: "No",
    projectId,
  };
}

function App() {
  const auth = getAuth();
  const [currentUser,    setCurrentUser]    = useState(null);
  const [userClaims,     setUserClaims]     = useState({ admin: false, memberships: {} });
  const [authLoading,    setAuthLoading]    = useState(true);
  const [loginEmail,     setLoginEmail]     = useState("");
  const [loginPassword,  setLoginPassword]  = useState("");
  const [loginError,     setLoginError]     = useState("");
  const [loginLoading,   setLoginLoading]   = useState(false);

  // Listen for Firebase auth state changes — also read the RBAC custom claims
  // (admin / memberships) off the ID token, which scope what data loads.
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user || null);
      if (user) {
        try {
          const res = await user.getIdTokenResult();
          setUserClaims({
            admin: res.claims.admin === true,
            memberships: (res.claims.memberships && typeof res.claims.memberships === "object") ? res.claims.memberships : {},
          });
        } catch {
          setUserClaims({ admin: false, memberships: {} });
        }
      } else {
        setUserClaims({ admin: false, memberships: {} });
      }
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, [auth]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoginLoading(true);
    try {
      await signInWithEmailAndPassword(auth, loginEmail, loginPassword);
    } catch (err) {
      setLoginError("Invalid email or password. Please try again.");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleSignOut = async () => {
    await signOut(auth);
    setCurrentUser(null);
  };

  // ── Demo sandbox — anonymous auth → a freshly cloned template project ──────
  // A public visitor arrives via the marketing site's ?demo=1 link (or taps
  // "Try the demo" below). We sign them in anonymously, ask provisionDemo to
  // clone the golden template into a private project, then refresh the ID token
  // so the new membership claim scopes the whole app to that one demo project.
  const [demoState, setDemoState] = useState({ status: "idle", projectId: "", expiresAt: null, error: "" });

  const startDemo = useCallback(async () => {
    setDemoState({ status: "provisioning", projectId: "", expiresAt: null, error: "" });
    try {
      const cred = await signInAnonymously(auth);
      const provision = httpsCallable(getFunctions(), "provisionDemo");
      const { data } = await provision();
      // Pick up the freshly-granted membership claim, then mirror it into state
      // so the projects listener loads the cloned demo project immediately.
      await cred.user.getIdToken(true);
      const res = await cred.user.getIdTokenResult();
      setUserClaims({
        admin: false,
        memberships: (res.claims.memberships && typeof res.claims.memberships === "object") ? res.claims.memberships : {},
      });
      setDemoState({ status: "ready", projectId: data.projectId, expiresAt: data.expiresAt || null, error: "" });
      // Drop ?demo from the URL so a refresh doesn't try to re-provision.
      try {
        const url = new URL(window.location.href);
        if (url.searchParams.has("demo")) {
          url.searchParams.delete("demo");
          window.history.replaceState({}, "", url.toString());
        }
      } catch { /* non-fatal */ }
    } catch (e) {
      setDemoState({ status: "error", projectId: "", expiresAt: null, error: (e && e.message) || "Could not start the demo. Please try again." });
    }
  }, [auth]);

  const resetDemoData = useCallback(async () => {
    // Resolve the demo project id from state, falling back to the membership
    // claim — so Reset still works after a page reload (when demoState is empty).
    const pid = demoState.projectId || Object.keys(userClaims.memberships || {})[0] || "";
    if (!pid) return;
    if (!window.confirm("Reset this demo to its original sample data? Any changes you've made will be discarded.")) return;
    try {
      const reset = httpsCallable(getFunctions(), "resetDemo");
      await reset({ projectId: pid });
      window.location.reload();
    } catch (e) {
      window.alert((e && e.message) || "Could not reset the demo right now.");
    }
  }, [demoState.projectId, userClaims]);

  // Auto-start a demo for visitors who follow the marketing site's ?demo=1
  // link — unless a real (non-anonymous) user is already signed in.
  useEffect(() => {
    if (authLoading || demoState.status !== "idle") return;
    let wantsDemo = false;
    try { wantsDemo = new URLSearchParams(window.location.search).get("demo") === "1"; } catch { /* ignore */ }
    if (!wantsDemo) return;
    if (currentUser && !currentUser.isAnonymous) return; // real user — don't hijack their session
    startDemo();
  }, [authLoading, currentUser, demoState.status, startDemo]);

  const isAnonDemo = !!(currentUser && currentUser.isAnonymous);
  const demoDaysLeft = demoState.expiresAt
    ? Math.max(0, Math.ceil((demoState.expiresAt - Date.now()) / (24 * 60 * 60 * 1000)))
    : null;

  const [activeSection, setActiveSection] = useState(() => {
    try {
      const urlParam = new URLSearchParams(window.location.search).get("section");
      if (urlParam) return urlParam;
      return window.localStorage.getItem("activeSection") || "home";
    } catch (error) {
      return "home";
    }
  });
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");

  const [projectForm, setProjectForm] = useState(makeProjectForm());
  const [isEditingProject, setIsEditingProject] = useState(false);
  const [isAddingProject, setIsAddingProject] = useState(false);

  const [variations, setVariations] = useProjectCollection(selectedProject, "variations", { withSetter: true });
  const claims = useProjectCollection(selectedProject, "claims");
  const [claimForm, setClaimForm] = useState(emptyClaimForm);
  const [editingClaimId, setEditingClaimId] = useState(null);
  const [certificates, setCertificates] = useProjectCollection(selectedProject, "certificates", { withSetter: true });
  const [rfis, setRfis] = useProjectCollection(selectedProject, "rfis", { withSetter: true });
const instructions = useProjectCollection(selectedProject, "instructions");
// Read-only per-project collections — fed only by their Firestore listener.
// Migrated from inline useState + onSnapshot to the shared useProjectCollection hook.
const diaries = useProjectCollection(selectedProject, "diaries");
const programmeActivities = useProjectCollection(selectedProject, "programme");
const progressAssets = useProjectCollection(selectedProject, "progressAssets");
const auditLogs = useProjectCollection(selectedProject, "auditLogs");
  const [forecastByProject, setForecastByProject] = useState({});
const additionalCashflowMonths = useProjectCollection(selectedProject, "additionalCashflowMonths", {
  sort: (a, b) => String(a.key || a.id || "").localeCompare(String(b.key || b.id || "")),
});
  const [boqSections, setBoqSections] = useState([]);
  const soqItems = useProjectCollection(selectedProject, "soqItems");
  const cpaClasses = useProjectCollection(selectedProject, "cpaClasses", {
    sort: (a, b) =>
      String(a.name || a.id || "").localeCompare(String(b.name || b.id || ""), undefined, {
        numeric: true,
        sensitivity: "base",
      }),
  });
const [cpaColumns, setCpaColumns] = useProjectCollection(selectedProject, "cpaColumns", { withSetter: true });
const [fidicCpaTables, setFidicCpaTables] = useState(() => createDefaultFidicCpaState());
const [roeRows, setRoeRows] = useProjectCollection(selectedProject, "roeRows", { withSetter: true });




  const [rfiForm, setRfiForm] = useState(makeRfiForm());
  const [editingRfiId, setEditingRfiId] = useState(null);
  const [instructionForm, setInstructionForm] = useState(makeInstructionForm());
  const [editingInstructionId, setEditingInstructionId] = useState(null);
  const [variationForm, setVariationForm] = useState(makeVariationForm());
  const [editingVariationId, setEditingVariationId] = useState(null);
  const [certificateForm, setCertificateForm] = useState(makeCertificateForm());
  const [editingCertificateId, setEditingCertificateId] = useState(null);
  const [diaryForm, setDiaryForm] = useState(makeDiaryForm());
  const [editingDiaryId, setEditingDiaryId] = useState(null);
  const [viewModal, setViewModal] = useState({ isOpen: false, title: "", item: null });

const ncrs = useProjectCollection(selectedProject, "ncrs");
const [ncrForm, setNcrForm] = useState(emptyNcrForm);
const [editingNcrId, setEditingNcrId] = useState(null);



  const currentProject = useMemo(
    () => projects.find((project) => project.id === selectedProject) || null,
    [projects, selectedProject]
  );

  const currentSectionLabel = useMemo(() => {
    const match = [
      { key: "home", label: "Home" },
      { key: "cashflow", label: "Cashflow" },
      { key: "certificates", label: "Certificates" },
      { key: "rfis", label: "RFIs" },
      { key: "instructions", label: "Instructions" },
      { key: "claims", label: "Claims" },
{ key: "claude", label: "Claude Claims Review" },
{ key: "projectTracker", label: "Project Tracker" },
      { key: "variations", label: "Variations" },
      { key: "programme", label: "Programme" },
      { key: "boq", label: "BOQ" },
      { key: "soqImport", label: "SOQ Import" },
      { key: "soqRegister", label: "SOQ Register" },
      { key: "cpa", label: "CPA" },
      { key: "roe", label: "ROE" },
      { key: "financial", label: "Financial" },
      { key: "contract", label: "Contract" },
      { key: "env", label: "Environmental" },
      { key: "hs", label: "Health & Safety" },
      { key: "diary", label: "Diary" },
      { key: "audit", label: "Audit Trail" },
      { key: "ncr", label: "NCRs" },
      { key: "quality", label: "Quality Tests" },
      { key: "drawings", label: "Drawings" },
      { key: "completion", label: "Completion & Defects" },
      { key: "timeBars", label: "Time-Bars & Notices" },
      { key: "finalAccount", label: "Final Account" },
      { key: "templates", label: "Templates" },
      { key: "projectRegister", label: "Project Register" },
      { key: "users", label: "User Management" },
      { key: "clients", label: "Client Management" },
    ].find((item) => item.key === activeSection);
    return match?.label || "Home";
  }, [activeSection]);

  useEffect(() => {
    try {
      window.localStorage.setItem("activeSection", activeSection);
    } catch (error) {
      // Ignore storage errors so navigation still works normally.
    }
  }, [activeSection]);

  // Non-admins must never sit on an admin-only section (e.g. a stale
  // activeSection restored from localStorage) — bounce them to Home.
  useEffect(() => {
    if (!userClaims.admin && (activeSection === "users" || activeSection === "clients" || activeSection === "projectRegister")) {
      setActiveSection("home");
    }
  }, [userClaims.admin, activeSection]);


  const monthRange = useMemo(() => {
    return buildMonthRange(
      currentProject?.startDate,
      currentProject?.revisedEndDate || currentProject?.estimatedEndDate || currentProject?.completionDate
    );
  }, [currentProject?.startDate, currentProject?.revisedEndDate, currentProject?.estimatedEndDate, currentProject?.completionDate]);

  const activeForecastBlocks = useMemo(() => {
    return buildBlockMap(monthRange, forecastByProject[selectedProject] || {});
  }, [monthRange, forecastByProject, selectedProject]);

 
  const writeAuditLog = async (projectId, entityType, entityId, action, message, metadata = {}) => {
    if (!projectId) return;
    // Primary path: seal the entry into the tamper-evident hash chain via the
    // appendAudit Cloud Function (identity is stamped server-side from the token).
    try {
      await appendAudit({ projectId, entityType, entityId, action, message, metadata });
      return;
    } catch (e) {
      // Fallback: never lose an audit record. A direct write is still
      // append-only (Firestore rules deny update/delete) but is left UNSEALED
      // (no seq/hash) — the chain verifier reports these separately rather than
      // treating them as corruption.
      console.warn("Audit chain append failed, writing unsealed fallback entry:", e?.message || e);
      const auditRef = doc(collection(db, "projects", projectId, "auditLogs"));
      await setDoc(auditRef, {
        id: auditRef.id,
        projectId,
        entityType,
        entityId,
        action,
        message,
        metadata,
        sealed: false,
        // Record who made the change — essential for forensic audit trail
        userId:       currentUser?.uid   || "unknown",
        userEmail:    currentUser?.email || "unknown",
        userName:     currentUser?.displayName || currentUser?.email || "unknown",
        createdAt: serverTimestamp(),
      });
    }
  };

  useEffect(() => {
    // Scope the projects list to what the caller may read, or the unfiltered
    // query is rejected (permission-denied) for non-admins by firestore.rules.
    //   admin            → every project
    //   member of some   → only those projects (documentId() "in", max 30)
    //   member of none   → nothing
    const memberPids = Object.keys(userClaims.memberships || {});
    let q;
    if (userClaims.admin) {
      q = query(collection(db, "projects"), orderBy("id"));
    } else if (memberPids.length) {
      q = query(collection(db, "projects"), where(documentId(), "in", memberPids.slice(0, 30)));
    } else {
      setProjects([]);
      setSelectedProject("");
      return undefined;
    }
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const nextProjects = snapshot.docs
        .map((item) => ({ id: item.id, ...item.data() }))
        .sort((a, b) => String(a.id).localeCompare(String(b.id)));
      setProjects(nextProjects);
      setSelectedProject((prev) => {
        if (prev && nextProjects.some((project) => project.id === prev)) return prev;
        return nextProjects[0]?.id || "";
      });
    }, (err) => console.error("projects listener:", err));
    return () => unsubscribe();
  }, [userClaims]);

  useEffect(() => {
    if (!selectedProject) {
      setProjectForm(makeProjectForm());
      setRfiForm(makeRfiForm());
      setInstructionForm(makeInstructionForm());
      setVariationForm(makeVariationForm());
      setCertificateForm(makeCertificateForm());
      setDiaryForm(makeDiaryForm());
setNcrForm({ ...emptyNcrForm });

      return;
    }

    if (currentProject && !isEditingProject && !isAddingProject) {
      setProjectForm(currentProject);
    }

    if (!editingRfiId) setRfiForm(makeRfiForm(selectedProject));
    if (!editingInstructionId) setInstructionForm(makeInstructionForm(selectedProject));
    if (!editingVariationId) setVariationForm(makeVariationForm(selectedProject));
    if (!editingCertificateId) setCertificateForm(makeCertificateForm(selectedProject));
    if (!editingDiaryId) setDiaryForm(makeDiaryForm(selectedProject));
if (!editingNcrId) setNcrForm({ ...emptyNcrForm, projectId: selectedProject });
  }, [
    selectedProject,
    currentProject,
    isEditingProject,
    isAddingProject,
    editingRfiId,
    editingInstructionId,
    editingVariationId,
    editingCertificateId,
    editingDiaryId,
  editingNcrId,
  editingClaimId,


  ]);

  useEffect(() => {
    if (!selectedProject) {
      setClaimForm({ ...emptyClaimForm });
      setEditingClaimId(null);
      setForecastByProject({});
      setBoqSections([]);
      setFidicCpaTables(createDefaultFidicCpaState());
      return;
    }

    // cpaClasses, ncrs, cpaColumns and additionalCashflowMonths are now owned by
    // the useProjectCollection hook (see the declarations above). Only the two
    // single-doc listeners (cpa/fidicTables, cashflow/forecast) and the dual-source
    // BOQ merge remain inline here — they don't fit the per-collection hook shape.
    const unsubscribers = [
onSnapshot(doc(db, "projects", selectedProject, "cpa", "fidicTables"), (snapshot) => {
  setFidicCpaTables(
    snapshot.exists()
      ? snapshot.data()?.tables || createDefaultFidicCpaState()
      : createDefaultFidicCpaState()
  );
}),




      onSnapshot(doc(db, "projects", selectedProject, "cashflow", "forecast"), (snapshot) => {
        const blocks = snapshot.exists() ? snapshot.data()?.blocks || {} : {};
        setForecastByProject((prev) => ({
          ...prev,
          [selectedProject]: blocks,
        }));
      }),
    ];

    let isCancelled = false;

    const mergeBoqSources = async () => {
      const primaryRef = collection(db, "projects", selectedProject, "boq");
      const legacyRef = collection(db, "projects", selectedProject, "boqSections");

      const [primarySnap, legacySnap] = await Promise.all([
        getDocs(primaryRef),
        getDocs(legacyRef).catch(() => ({ docs: [] })),
      ]);

      if (isCancelled) return;

      const merged = [...primarySnap.docs, ...(legacySnap.docs || [])].reduce((acc, item) => {
        acc[item.id] = { id: item.id, ...item.data() };
        return acc;
      }, {});

      setBoqSections(Object.values(merged));
    };

    mergeBoqSources().catch((error) => {
      console.error("Error loading BOQ sections", error);
      if (!isCancelled) setBoqSections([]);
    });

    return () => {
      isCancelled = true;
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [selectedProject]);

  const beginAddProject = () => {
    setProjectForm(makeProjectForm());
    setIsAddingProject(true);
    setIsEditingProject(false);
    setActiveSection("home");
  };

  const beginEditProject = () => {
    if (!currentProject) return;
    setProjectForm(currentProject);
    setIsEditingProject(true);
    setIsAddingProject(false);
    setActiveSection("home");
  };

  // Select a project from the admin register and jump to its Home view.
  const openProjectFromRegister = (projectId) => {
    if (!projectId) return;
    setSelectedProject(projectId);
    setIsAddingProject(false);
    setIsEditingProject(false);
    setActiveSection("home");
  };

  // Select a project from the admin register and open it straight in edit mode.
  const editProjectFromRegister = (projectId) => {
    const target = projects.find((p) => p.id === projectId);
    if (!target) return;
    setSelectedProject(projectId);
    setProjectForm(target);
    setIsEditingProject(true);
    setIsAddingProject(false);
    setActiveSection("home");
  };

  const saveProject = async () => {
    if (!projectForm.id || !projectForm.name) return;

    const cpaType = normaliseCpaType(projectForm.cpaType || projectForm.contractType);
    const contractDurationDays =
      projectForm.contractDurationDays === undefined ||
      projectForm.contractDurationDays === null ||
      String(projectForm.contractDurationDays).trim() === ""
        ? ""
        : numberOrZero(projectForm.contractDurationDays);
    const daysToSkip = normaliseDaysToSkip(projectForm.daysToSkip || {});
    const buildersBreakCount = numberOrZero(projectForm.buildersBreakCount);
    const buildersBreaks = normaliseBuildersBreaks(projectForm.buildersBreaks || [], buildersBreakCount);
    const projectSchedule = {
      ...projectForm,
      contractDurationDays,
      daysToSkip,
      buildersBreakCount,
      buildersBreaks,
    };
    const hasPhasedDueCompletion = !!projectForm.dueCompletionPhased;
    const calculatedEstimatedEndDate = hasPhasedDueCompletion
      ? ""
      : calculateEstimatedEndDate(projectSchedule);
    const estimatedEndDate =
      hasPhasedDueCompletion
        ? projectForm.estimatedEndDate || projectForm.completionDate || ""
        : calculatedEstimatedEndDate ||
          projectForm.estimatedEndDate ||
          projectForm.completionDate ||
          "";
    const awardedDays = sumAwardedClaimDays(projectForm.id, claims);
    const revisedEndDate =
      !estimatedEndDate
        ? ""
        : awardedDays > 0
        ? addContractDaysAfter(estimatedEndDate, awardedDays, projectSchedule)
        : estimatedEndDate;

    // Use project-configured rates rather than hardcoded values
    const contingencyRatePct = Number(projectForm.contingencyRate ?? 10);
    const vatRatePct         = Number(projectForm.vatRate ?? 15);
    const contingencyRateDec = (Number.isFinite(contingencyRatePct) && contingencyRatePct > 0 ? contingencyRatePct : 10) / 100;
    const vatRateDec         = (Number.isFinite(vatRatePct) && vatRatePct > 0 ? vatRatePct : 15) / 100;
    const scheduledWorksFromBoq = boqTenderedTotal || numberOrZero(projectForm.scheduledWorks);
    const contingenciesFromBoq = scheduledWorksFromBoq * contingencyRateDec;
    const vatFromBoq = (scheduledWorksFromBoq + contingenciesFromBoq) * vatRateDec;
    const grandTotalFromBoq = scheduledWorksFromBoq + contingenciesFromBoq + vatFromBoq;

    await setDoc(
      doc(db, "projects", projectForm.id),
      {
        ...projectForm,
        id: projectForm.id,
        cpaType,
        contractDurationDays,
        contractDurationLabel: projectForm.contractDurationLabel || "",
        contractDurationValue: projectForm.contractDurationValue ?? "",
        contractDurationUnit: projectForm.contractDurationUnit || "",
        daysToSkip,
        buildersBreakCount,
        buildersBreaks,
        estimatedEndDate,
        revisedEndDate,
        completionDate: estimatedEndDate,
        scheduledWorks: scheduledWorksFromBoq,
        contingencies: contingenciesFromBoq,
        vat: vatFromBoq,
        grandTotal: grandTotalFromBoq,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    await writeAuditLog(
      projectForm.id,
      "project",
      projectForm.id,
      isAddingProject ? "create" : "update",
      `${isAddingProject ? "Created" : "Updated"} project ${projectForm.id}`,
      {
        name: projectForm.name,
        client: projectForm.client,
        department: projectForm.department,
        contractNumber: projectForm.contractNumber,
        contractDescription: projectForm.contractDescription,
        contractor: projectForm.contractor,
        contractType: projectForm.contractType,
        cpaType,
        contractDurationDays,
        contractDurationLabel: projectForm.contractDurationLabel || "",
        contractDurationValue: projectForm.contractDurationValue ?? "",
        contractDurationUnit: projectForm.contractDurationUnit || "",
        daysToSkip,
        buildersBreakCount,
        buildersBreaks,
        estimatedEndDate,
        revisedEndDate,
        scheduledWorks: scheduledWorksFromBoq,
        contingencies: contingenciesFromBoq,
        vat: vatFromBoq,
        grandTotal: grandTotalFromBoq,
      }
    );

    setSelectedProject(projectForm.id);
    setIsAddingProject(false);
    setIsEditingProject(false);
  };

  const cancelProjectEdit = () => {
    setIsAddingProject(false);
    setIsEditingProject(false);
    setProjectForm(currentProject || makeProjectForm(selectedProject));
  };

  const deleteProject = async (projectId) => {
    if (!projectId) return;
    const target = projects.find((p) => p.id === projectId);
    // Record the deletion before removing the doc (audit logs live in a
    // subcollection that survives the parent delete).
    await writeAuditLog(
      projectId, "project", projectId, "delete",
      `Deleted project ${target?.name ? `${projectId} — ${target.name}` : projectId}`
    );
    await deleteDoc(doc(db, "projects", projectId));
    // The projects listener resets selection; if we were on the deleted
    // project's screens, fall back to the register.
    if (selectedProject === projectId) setActiveSection("projectRegister");
  };

  const handleViewItem = useStableCallback((title, item) => setViewModal({ isOpen: true, title, item }));
  const closeViewModal = useStableCallback(() => setViewModal({ isOpen: false, title: "", item: null }));

  const handleAddRfi = useStableCallback(async () => {
    if (!selectedProject) return;
    // New RFIs are auto-numbered (001, 002, …) per project; editing keeps the
    // existing locked number. nextRfiNumber() reads the same `rfis` list the
    // form previews, so the number shown is the number saved.
    const rfiNo = editingRfiId ? rfiForm.rfiNo : nextRfiNumber(rfis);
    if (!rfiNo) return;
    const rfiId = editingRfiId || rfiNo;
    const payload = { ...rfiForm, rfiNo, id: rfiId, projectId: selectedProject, updatedAt: serverTimestamp() };
    await setDoc(doc(db, "projects", selectedProject, "rfis", rfiId), payload, { merge: true });
    await writeAuditLog(selectedProject, "rfi", rfiId, editingRfiId ? "update" : "create", `Saved RFI ${rfiNo}`);
    setRfiForm(makeRfiForm(selectedProject));
    setEditingRfiId(null);
  });

const handleCreateRfiFromProjectTracker = useStableCallback(async (trackerPayload = {}) => {
  if (!selectedProject || !trackerPayload) return;

  const rfiNo = String(
    trackerPayload.rfiNo ||
      trackerPayload.rfiNumber ||
      trackerPayload.number ||
      nextRfiNumber(rfis)
  ).trim();

  if (!rfiNo) return;

  const rfiId = trackerPayload.id || rfiNo;

  const payload = {
    ...makeRfiForm(selectedProject),
    ...trackerPayload,
    id: rfiId,
    rfiNo,
    rfiNumber: trackerPayload.rfiNumber || rfiNo,
    projectId: selectedProject,
    status: trackerPayload.status || "Open",
    updatedAt: serverTimestamp(),
    createdFrom: trackerPayload.source || "ProjectTracker",
  };

  await setDoc(doc(db, "projects", selectedProject, "rfis", rfiId), payload, { merge: true });

  setRfis((prev) => {
    const existing = (prev || []).some((item) => item.id === rfiId);
    if (existing) {
      return (prev || []).map((item) => (item.id === rfiId ? { ...item, ...payload } : item));
    }
    return [...(prev || []), payload];
  });

  await writeAuditLog(
    selectedProject,
    "rfi",
    rfiId,
    "create_from_project_tracker",
    `Created RFI ${rfiNo} from Project Tracker correspondence`,
    {
      rfiNo,
      linkedCorrespondenceItemNo: payload.linkedCorrespondenceItemNo || "",
      drawingReference: payload.drawingReference || payload.reference || "",
      source: payload.source || "ProjectTracker",
    }
  );
});


  const handleEditRfi = useStableCallback((row) => {
    setEditingRfiId(row.id);
    setRfiForm({ ...makeRfiForm(selectedProject), ...row });
  });

  const handleDeleteRfi = useStableCallback(async (id) => {
    if (!selectedProject) return;
    await deleteDoc(doc(db, "projects", selectedProject, "rfis", id));
    await writeAuditLog(selectedProject, "rfi", id, "delete", `Deleted RFI ${id}`);
    if (editingRfiId === id) {
      setRfiForm(makeRfiForm(selectedProject));
      setEditingRfiId(null);
    }
  });

  const clearRfiForm = useStableCallback(() => {
    setRfiForm(makeRfiForm(selectedProject));
    setEditingRfiId(null);
  });

  const handleCreateVariationFromRfi = useStableCallback((rfiRow) => {
    setVariationForm({
      ...makeVariationForm(selectedProject),
      projectId: selectedProject,
      title: rfiRow.subject || "Variation from RFI",
      description: rfiRow.responseSummary || rfiRow.subject || "",
      voDescription: rfiRow.subject || "Variation from RFI",
      motivation: rfiRow.reason || "",
      rfiRef: rfiRow.rfiNo || "",
      contractor: rfiRow.contractor || "",
      boqSection: rfiRow.reference || "",
    });
    setEditingVariationId(null);
    setActiveSection("variations");
  });

  const handleAddInstruction = useStableCallback(async () => {
    if (!selectedProject) return;
    // New site instructions are auto-numbered per project using the selected
    // project format; editing keeps the existing locked number.
    const siConfig = getInstructionConfig(currentProject);
    const siNumber = editingInstructionId
      ? instructionForm.siNumber
      : suggestNextSiNumber(siConfig, instructions, currentProject);
    if (!siNumber) return;
    const instructionId = editingInstructionId || siNumber;
    const payload = {
      ...instructionForm,
      siNumber,
      id: instructionId,
      projectId: selectedProject,
      projectNumber: currentProject?.id || instructionForm.projectNumber || "",
      contractNumber: currentProject?.contractNumber || currentProject?.contractNo || instructionForm.contractNumber || "",
      contractDescription: currentProject?.name || currentProject?.contractDescription || instructionForm.contractDescription || "",
      contractor: currentProject?.contractor || instructionForm.contractor || "",
      updatedAt: serverTimestamp(),
    };
    await setDoc(doc(db, "projects", selectedProject, "instructions", instructionId), payload, { merge: true });
    await writeAuditLog(selectedProject, "instruction", instructionId, editingInstructionId ? "update" : "create", `Saved instruction ${siNumber}`);
    setInstructionForm(makeInstructionForm(selectedProject));
    setEditingInstructionId(null);
  });

  const handleEditInstruction = useStableCallback((row) => {
    setEditingInstructionId(row.id);
    setInstructionForm({ ...makeInstructionForm(selectedProject), ...row });
  });

  const handleDeleteInstruction = useStableCallback(async (id) => {
    if (!selectedProject) return;
    await deleteDoc(doc(db, "projects", selectedProject, "instructions", id));
   
    await writeAuditLog(selectedProject, "instruction", id, "delete", `Deleted instruction ${id}`);
    if (editingInstructionId === id) {
      setInstructionForm(makeInstructionForm(selectedProject));
      setEditingInstructionId(null);
    }
  });

  const clearInstructionForm = useStableCallback(() => {
    setInstructionForm(makeInstructionForm(selectedProject));
    setEditingInstructionId(null);
  });

const resolveEstimatedEndDate = (project) => {
  const phasedSchedule = buildGccPhasedScheduleSummary(project);
  return (
    phasedSchedule?.finalDueDate ||
    project?.estimatedEndDate ||
    project?.completionDate ||
    project?.revisedEndDate ||
    ""
  );
};

const addDaysToDate = (dateString, days) => {
  if (!dateString) return "";
  const base = new Date(dateString);
  if (Number.isNaN(base.getTime())) return "";
  base.setDate(base.getDate() + numberOrZero(days));
  return base.toISOString().slice(0, 10);
};

const sumAwardedClaimDays = (projectId, sourceClaims) => {
  return (sourceClaims || [])
    .filter((item) => item.projectId === projectId)
    .filter((item) => String(item.status || "") === "Awarded")
    .reduce((sum, item) => sum + numberOrZero(item.daysAwarded || item.daysClaimed), 0);
};

const computeProposedRulingDate = (claimDate) => {
  if (!claimDate) return "";
  return addDaysToDate(claimDate, 28);
};

const updateProjectDatesFromClaims = async (projectId, nextClaims) => {
  const project = projects.find((item) => item.id === projectId);
  if (!project) return;

  const estimatedEndDate = project.dueCompletionPhased
    ? resolveEstimatedEndDate(project)
    : calculateEstimatedEndDate(project) || resolveEstimatedEndDate(project);
  const awardedDays = sumAwardedClaimDays(projectId, nextClaims);
  const revisedEndDate = !estimatedEndDate
    ? ""
    : awardedDays > 0
    ? addContractDaysAfter(estimatedEndDate, awardedDays, project)
    : estimatedEndDate;

  const updatedProject = {
    ...project,
    estimatedEndDate,
    revisedEndDate,
    completionDate: estimatedEndDate,
  };

  setProjects((prev) => prev.map((item) => (item.id === projectId ? updatedProject : item)));

  if (currentProject?.id === projectId && !isEditingProject && !isAddingProject) {
    setProjectForm(updatedProject);
  }

  await setDoc(
    doc(db, "projects", projectId),
    {
      estimatedEndDate: updatedProject.estimatedEndDate,
      revisedEndDate: updatedProject.revisedEndDate,
      completionDate: updatedProject.completionDate,
    },
    { merge: true }
  );
};

const clearClaimForm = () => {
  setClaimForm({ ...emptyClaimForm, projectId: selectedProject });
  setEditingClaimId(null);
};

const handleAddOrUpdateClaim = useStableCallback(async () => {
  if (!selectedProject) return;
  if (!claimForm.claimNo || !claimForm.detail) return;

  const payload = {
    ...emptyClaimForm,
    ...claimForm,
    projectId: selectedProject,
    daysClaimed: numberOrZero(claimForm.daysClaimed),
    daysAwarded: numberOrZero(claimForm.daysAwarded),
    pgClaimed: numberOrZero(claimForm.pgClaimed),
    pgAwarded: numberOrZero(claimForm.pgAwarded),
    directCostClaimed: numberOrZero(claimForm.directCostClaimed),
    directCostAwarded: numberOrZero(claimForm.directCostAwarded),
    notificationCount: String(numberOrZero(claimForm.notificationCount)),
    notifications: Array.isArray(claimForm.notifications) ? claimForm.notifications : [],
    proposedRulingDate:
      claimForm.proposedRulingDate || computeProposedRulingDate(claimForm.claimDate),
    updatedAt: serverTimestamp(),
  };

  const claimId = editingClaimId || claimForm.claimNo || `CLM-${Date.now()}`;

  await setDoc(
    doc(db, "projects", selectedProject, "claims", claimId),
    { ...payload, id: claimId },
    { merge: true }
  );

  await writeAuditLog(
    selectedProject,
    "claim",
    claimId,
    editingClaimId ? "update" : "create",
    `${editingClaimId ? "Updated" : "Created"} claim ${payload.claimNo}`,
    {
      claimNo: payload.claimNo,
      status: payload.status,
      daysClaimed: payload.daysClaimed,
      daysAwarded: payload.daysAwarded,
      pgClaimed: payload.pgClaimed,
      pgAwarded: payload.pgAwarded,
      proposedRulingDate: payload.proposedRulingDate,
      notificationCount: payload.notificationCount,
    }
  );

  const nextClaims = editingClaimId
    ? claims.map((item) => (item.id === editingClaimId ? { ...payload, id: claimId } : item))
    : [...claims, { ...payload, id: claimId }];

  await updateProjectDatesFromClaims(selectedProject, nextClaims);
  clearClaimForm();
});

const handleEditClaim = useStableCallback((row) => {
  setEditingClaimId(row.id);
  setClaimForm({
    ...emptyClaimForm,
    ...row,
    projectId: selectedProject,
    notificationCount: String(
      numberOrZero(
        row.notificationCount || (Array.isArray(row.notifications) ? row.notifications.length : 0)
      )
    ),
    notifications: Array.isArray(row.notifications) ? row.notifications : [],
  });
});

const handleDeleteClaim = useStableCallback(async (id) => {
  if (!selectedProject) return;
  const claimToDelete = (claims || []).find((item) => item.id === id);

  await deleteDoc(doc(db, "projects", selectedProject, "claims", id));

  await writeAuditLog(
    selectedProject,
    "claim",
    id,
    "delete",
    `Deleted claim ${claimToDelete?.claimNo || id}`,
    { claimNo: claimToDelete?.claimNo || "" }
  );

  const nextClaims = claims.filter((item) => item.id !== id);
  await updateProjectDatesFromClaims(selectedProject, nextClaims);

  if (editingClaimId === id) {
    clearClaimForm();
  }
});



const getNextNcrNumber = () => {
  const projectNcrs = (ncrs || []).filter((item) => item.projectId === selectedProject);
  return `NCR-${String(projectNcrs.length + 1).padStart(3, "0")}`;
};

const handleAddOrUpdateNcr = useStableCallback(async () => {
  if (!selectedProject) return;
  if (!(ncrForm.description || ncrForm.ncrNo)) return;

  const ncrId = editingNcrId || ncrForm.ncrNo || getNextNcrNumber();
  const payload = {
    ...emptyNcrForm,
    ...ncrForm,
    id: ncrId,
    ncrNo: ncrForm.ncrNo || getNextNcrNumber(),
    projectId: selectedProject,
    updatedAt: serverTimestamp(),
  };

  await setDoc(doc(db, "projects", selectedProject, "ncrs", ncrId), payload, { merge: true });

  await writeAuditLog(
    selectedProject,
    "ncr",
    ncrId,
    editingNcrId ? "update" : "create",
    `${editingNcrId ? "Updated" : "Created"} NCR ${payload.ncrNo}`,
    {
      ncrNo: payload.ncrNo,
      status: payload.status,
      description: payload.description,
      specificationReference: payload.specificationReference,
      drawingReference: payload.drawingReference,
      linkedVariationId: payload.linkedVariationId || "",
    }
  );

  setNcrForm({ ...emptyNcrForm, projectId: selectedProject, ncrNo: getNextNcrNumber() });
  setEditingNcrId(null);
});

const handleEditNcr = useStableCallback((row) => {
  setEditingNcrId(row.id);
  setNcrForm({ ...emptyNcrForm, ...row, projectId: selectedProject });
});

const handleDeleteNcr = useStableCallback(async (id) => {
  if (!selectedProject) return;
  const ncrToDelete = (ncrs || []).find((item) => item.id === id);

  await deleteDoc(doc(db, "projects", selectedProject, "ncrs", id));

  await writeAuditLog(
    selectedProject,
    "ncr",
    id,
    "delete",
    `Deleted NCR ${ncrToDelete?.ncrNo || id}`,
    { ncrNo: ncrToDelete?.ncrNo || "" }
  );

  if (editingNcrId === id) {
    setNcrForm({ ...emptyNcrForm, projectId: selectedProject, ncrNo: getNextNcrNumber() });
    setEditingNcrId(null);
  }
});

const clearNcrForm = useStableCallback(() => {
  setNcrForm({ ...emptyNcrForm, projectId: selectedProject, ncrNo: getNextNcrNumber() });
  setEditingNcrId(null);
});

const handleCreateVariationFromNcr = useStableCallback(async (ncrRow) => {
  if (!selectedProject) return;

  const nextVariationId = `VO-${Date.now()}`;
  const nextVariationNo = `VO-${String((variations || []).length + 1).padStart(3, "0")}`;

  const variationPayload = {
    ...makeVariationForm(selectedProject),
    id: nextVariationId,
    projectId: selectedProject,
    variationNo: nextVariationNo,
    title: ncrRow.description || "Variation arising from NCR",
    voDescription: ncrRow.description || "Variation arising from NCR",
    description:
      ncrRow.correctiveAction ||
      ncrRow.description ||
      "Variation arising from contractor non-conformance",
    motivation:
      ncrRow.rootCause ||
      ncrRow.engineerRemarks ||
      "Variation created from NCR with commercial implications.",
    specificationRef: ncrRow.specificationReference || "",
    drawingRef: ncrRow.drawingReference || "",
    clauseRef: ncrRow.clauseReference || "",
    contractor: ncrRow.contractor || "",
    status: "Draft",
    updatedAt: serverTimestamp(),
  };

  await setDoc(
    doc(db, "projects", selectedProject, "variations", nextVariationId),
    variationPayload,
    { merge: true }
  );

  await setDoc(
    doc(db, "projects", selectedProject, "ncrs", ncrRow.id),
    {
      linkedVariationId: nextVariationId,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );

  await writeAuditLog(
    selectedProject,
    "variation",
    nextVariationId,
    "create",
    `Created variation ${nextVariationNo} from NCR ${ncrRow.ncrNo || ncrRow.id}`,
    {
      variationNo: nextVariationNo,
      sourceNcr: ncrRow.ncrNo || ncrRow.id,
    }
  );

  await writeAuditLog(
    selectedProject,
    "ncr",
    ncrRow.id,
    "link_variation",
    `Linked NCR ${ncrRow.ncrNo || ncrRow.id} to variation ${nextVariationNo}`,
    {
      ncrNo: ncrRow.ncrNo || ncrRow.id,
      linkedVariationId: nextVariationId,
    }
  );

  setVariationForm({ ...makeVariationForm(selectedProject), ...variationPayload });
  setEditingVariationId(nextVariationId);
  setActiveSection("variations");
});




  const handleCreateVariationFromInstruction = useStableCallback((instructionRow) => {
    setVariationForm({
      ...makeVariationForm(selectedProject),
      projectId: selectedProject,
      title: instructionRow.subject || "Variation from Instruction",
      description: instructionRow.instructionText || instructionRow.subject || "",
      voDescription: instructionRow.subject || "Variation from Instruction",
      motivation: instructionRow.instructionText || "",
      instructionRef: instructionRow.siNumber || "",
      contractor: instructionRow.contractor || "",
    });
    setEditingVariationId(null);
    setActiveSection("variations");
  });

  const handleAddOrUpdateVariation = useStableCallback(async (formOverride = null) => {
    const sourceForm = formOverride || variationForm;
    if (!selectedProject || !(sourceForm.variationNo || sourceForm.title || sourceForm.voDescription)) return;
    const variationId = editingVariationId || sourceForm.variationNo || `VO-${Date.now()}`;
    const additionalWorkAmount = numberOrZero(sourceForm.additionalWorkAmount);
    const existingRatesAmount = numberOrZero(sourceForm.existingRatesAmount);
    const calculatedOmittedWorkAmount = Array.isArray(sourceForm.omissionItems)
      ? sourceForm.omissionItems.reduce(
          (sum, entry) => sum + numberOrZero(entry.omittedAmount),
          0
        )
      : numberOrZero(sourceForm.omittedWorkAmount);
   const omittedWorkAmount = calculatedOmittedWorkAmount;

const grossVoAmount = additionalWorkAmount + existingRatesAmount;

const paymentCertificateVoAmount = grossVoAmount;

const netVoAmount = Math.max(0, grossVoAmount - omittedWorkAmount);

    const approvedValueInput = numberOrZero(sourceForm.approvedValue);
    const estimatedValueInput = numberOrZero(sourceForm.estimatedValue);
    const quotedValueInput = numberOrZero(sourceForm.quotedValue);
    const assessedValueInput = numberOrZero(sourceForm.assessedValue);

  const finalApprovedValue =
  approvedValueInput ||
  paymentCertificateVoAmount ||
  grossVoAmount ||
  assessedValueInput ||
  quotedValueInput ||
  estimatedValueInput ||
  0;

    const payload = {
      ...sourceForm,
      id: variationId,
      projectId: selectedProject,
      approvedValue: finalApprovedValue,
      estimatedValue: estimatedValueInput,
      quotedValue: quotedValueInput,
      assessedValue: assessedValueInput,
      additionalWorkAmount,
existingRatesAmount,
grossVoAmount,
paymentCertificateVoAmount,
omittedWorkAmount,
omissionItems: Array.isArray(sourceForm.omissionItems) ? sourceForm.omissionItems : [],
netVoAmount,
updatedAt: serverTimestamp(),
    };
    await setDoc(doc(db, "projects", selectedProject, "variations", variationId), payload, { merge: true });
    await writeAuditLog(selectedProject, "variation", variationId, editingVariationId ? "update" : "create", `Saved variation ${variationId}`);
    setVariationForm(makeVariationForm(selectedProject));
    setEditingVariationId(null);
  });

  const handleEditVariation = useStableCallback((row) => {
    setEditingVariationId(row.id);
    setVariationForm({ ...makeVariationForm(selectedProject), ...row });
  });

  const handleDeleteVariation = useStableCallback(async (id) => {
    if (!selectedProject) return;
    await deleteDoc(doc(db, "projects", selectedProject, "variations", id));
    await writeAuditLog(selectedProject, "variation", id, "delete", `Deleted variation ${id}`);
    if (editingVariationId === id) {
      setVariationForm(makeVariationForm(selectedProject));
      setEditingVariationId(null);
    }
  });

  const handleBulkUpdateVariations = useCallback(
    async (selectedIds = [], updatesById = {}) => {
      if (!selectedProject) return;

      const safeIds = Array.from(new Set((selectedIds || []).filter(Boolean)));
      if (safeIds.length === 0) return;

      await Promise.all(
        safeIds.map(async (variationId) => {
          const updates = updatesById?.[variationId] || {};
          if (!updates || Object.keys(updates).length === 0) return;

          await setDoc(
            doc(db, "projects", selectedProject, "variations", variationId),
            {
              ...updates,
              projectId: selectedProject,
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );
        })
      );

      setVariations((prev) =>
        (prev || []).map((item) =>
          safeIds.includes(item.id)
            ? {
                ...item,
                ...(updatesById?.[item.id] || {}),
              }
            : item
        )
      );

      await writeAuditLog(
        selectedProject,
        "variation",
        safeIds.join(", "),
        "bulk_update",
        `Bulk updated ${safeIds.length} variation${safeIds.length === 1 ? "" : "s"}`,
        {
          selectedIds: safeIds,
          updatesById,
        }
      );
    },
    [selectedProject]
  );

const handleAddOrUpdateCertificate = useStableCallback(async () => {
    if (!selectedProject || !certificateForm.certificateNo) return;
    const certificateId = editingCertificateId || certificateForm.certificateNo;

const matchingCpaColumn = computedCpaColumns.find(
  (item) =>
    String(item.certificateNo || "") === String(certificateForm.certificateNo || "") ||
    String(item.certificateMonth || "") === String(certificateForm.certificateMonth || "")
);

const isFidicContract = currentCpaType === "FIDIC";
const certificateNoKey = String(certificateForm.certificateNo || certificateId || "").trim();
const fidicCertificateCpa = fidicCpaByCertificateNo[certificateNoKey] || {};

const orderedCertificateNos = Object.keys(fidicCpaByCertificateNo).sort((a, b) =>
  String(a).localeCompare(String(b), undefined, {
    numeric: true,
    sensitivity: "base",
  })
);

const fidicCumulativeToCertificate = orderedCertificateNos.reduce((sum, certNo) => {
  const nextSum = sum + numberOrZero(fidicCpaByCertificateNo[certNo]?.cpaThisCertificate);
  return String(certNo).localeCompare(String(certificateNoKey), undefined, {
    numeric: true,
    sensitivity: "base",
  }) <= 0
    ? nextSum
    : sum;
}, 0);

const calculatedCpaCumulative = isFidicContract
  ? numberOrZero(fidicCumulativeToCertificate)
  : numberOrZero(matchingCpaColumn?.computed?.totalCumulativeCpa);
const calculatedCpaCertifiedValue = isFidicContract
  ? numberOrZero(fidicCertificateCpa.cpaCertifiedValue || fidicCertificateCpa.cpaThisCertificate)
  : numberOrZero(matchingCpaColumn?.computed?.certifiedValueIncludingCpa);
const calculatedCpaThisCertificate = isFidicContract
  ? numberOrZero(fidicCertificateCpa.cpaThisCertificate)
  : numberOrZero(matchingCpaColumn?.computed?.cpaTotal ?? matchingCpaColumn?.computed?.certifiedValueIncludingCpa);

const lockedCpaCumulative = certificateForm.lockedCpaCumulative !== undefined &&
  certificateForm.lockedCpaCumulative !== null &&
  String(certificateForm.lockedCpaCumulative) !== ""
    ? numberOrZero(certificateForm.lockedCpaCumulative)
    : calculatedCpaCumulative;
const lockedCpaCertifiedValue = certificateForm.lockedCpaCertifiedValue !== undefined &&
  certificateForm.lockedCpaCertifiedValue !== null &&
  String(certificateForm.lockedCpaCertifiedValue) !== ""
    ? numberOrZero(certificateForm.lockedCpaCertifiedValue)
    : calculatedCpaCertifiedValue;
const lockedCpaThisCertificate = certificateForm.lockedCpaThisCertificate !== undefined &&
  certificateForm.lockedCpaThisCertificate !== null &&
  String(certificateForm.lockedCpaThisCertificate) !== ""
    ? numberOrZero(certificateForm.lockedCpaThisCertificate)
    : calculatedCpaThisCertificate;
const lockedCpaIndexStatus = certificateForm.lockedCpaIndexStatus || getCpaIndexStatusForCertificateForApp(
  currentCpaType,
  certificateNoKey,
  certificateForm.certificateMonth,
  computedCpaColumns,
  fidicCpaTables
) || "P";

const payload = {
  ...certificateForm,
  id: certificateId,
  projectId: selectedProject,
  subtotalA: numberOrZero(certificateForm.subtotalA),
  subtotalB: isFidicContract
    ? numberOrZero(certificateForm.subtotalA) + numberOrZero(fidicCumulativeToCertificate)
    : numberOrZero(certificateForm.subtotalB),
  cpaCumulative: isFidicContract
    ? numberOrZero(fidicCumulativeToCertificate)
    : numberOrZero(matchingCpaColumn?.computed?.totalCumulativeCpa),
  cpaCertifiedValue: isFidicContract
    ? numberOrZero(fidicCertificateCpa.cpaCertifiedValue || fidicCertificateCpa.cpaThisCertificate)
    : numberOrZero(matchingCpaColumn?.computed?.certifiedValueIncludingCpa),
  cpaThisCertificate: isFidicContract
    ? numberOrZero(fidicCertificateCpa.cpaThisCertificate)
    : numberOrZero(matchingCpaColumn?.computed?.cpaTotal ?? matchingCpaColumn?.computed?.certifiedValueIncludingCpa),
  updatedAt: serverTimestamp(),
};
    
    await setDoc(doc(db, "projects", selectedProject, "certificates", certificateId), payload, { merge: true });
    await writeAuditLog(selectedProject, "certificate", certificateId, editingCertificateId ? "update" : "create", `Saved certificate ${certificateId}`);

    // ── Auto-CPA (GCC): a NEW certificate spawns its CPA column for the month of
    // processing, pulls that month's StatsSA indices (cache-first, best-effort),
    // and stamps the computed CPA straight back onto the certificate — so the
    // engineer sees the escalation without touching the CPA page. Edits and
    // months that already have a column are left alone.
    if (!isFidicContract && !editingCertificateId && !matchingCpaColumn) {
      try {
        const columns = cpaColumns || [];
        const lastColumn = columns.length ? columns[columns.length - 1] : null;
        const sharedBaseSets = columns.find((c) => Array.isArray(c.baseIndexSets) && c.baseIndexSets.length)?.baseIndexSets || null;
        const newColumn = {
          ...createEmptyCpaColumn(columns.length, lastColumn, sharedBaseSets),
          certificateNo: String(certificateForm.certificateNo || ""),
          certificateDate: certificateForm.certificateDate || "",
          certificateMonth: certificateForm.certificateMonth || "",
          // Cumulative escalatable base captured with the certificate (Phase 3).
          totalScheduledItems: String(numberOrZero(certificateForm.cpaScheduledItemsCumulative ?? certificateForm.subtotalA)),
        };

        // Month of processing → that month's indices (project's Table 6 row applied).
        const parsedMonth = parseIndexMonth(newColumn.certificateMonth || "") || parseIndexMonth(newColumn.certificateDate || "");
        if (parsedMonth) {
          try {
            const projectMaterialKey = currentProject?.statssaMaterialSeries || MATERIAL_SERIES_DEFAULT_KEY;
            let result = await fetchStatssaIndices({ ym: parsedMonth.ym, monthName: parsedMonth.monthName, year: parsedMonth.year });
            // Pre-Table 6 cached months can't serve a non-default material row —
            // refresh the cache once to pick up the full table.
            if (projectMaterialKey !== MATERIAL_SERIES_DEFAULT_KEY && result.cached && (!result.materialTable6 || result.materialTable6[projectMaterialKey] == null)) {
              result = await fetchStatssaIndices({ ym: parsedMonth.ym, monthName: parsedMonth.monthName, year: parsedMonth.year, force: true });
            }
            const { patch, provisional } = mapResultToIndexValues(
              STATSSA_SERIES_KEYS,
              result,
              { materialSeriesKey: projectMaterialKey }
            );
            Object.assign(newColumn, patch);
            newColumn.statssaProvisional = provisional;
            const anyProvisional = Object.values(provisional).some(Boolean);
            newColumn.indexStatus = anyProvisional ? "UL" : "L";
          } catch (fetchErr) {
            console.warn("StatsSA auto-fill at certificate save failed (column created without indices):", fetchErr);
          }
        }

        await handleSetCpaColumns([...columns, newColumn]);

        // Stamp the computed CPA back onto the just-saved certificate.
        const previousComputed = computedCpaColumns.length ? computedCpaColumns[computedCpaColumns.length - 1].computed : null;
        const computed = calculateCpaColumnForApp(newColumn, previousComputed);
        await setDoc(
          doc(db, "projects", selectedProject, "certificates", certificateId),
          {
            cpaCumulative: numberOrZero(computed?.totalCumulativeCpa),
            cpaThisCertificate: numberOrZero(computed?.cpaTotal ?? computed?.certifiedValueIncludingCpa),
            cpaCertifiedValue: numberOrZero(computed?.certifiedValueIncludingCpa),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
        await writeAuditLog(selectedProject, "certificate", certificateId, "auto_cpa", `Auto-created CPA column for ${newColumn.certificateMonth || "unspecified month"} and applied CPA ${numberOrZero(computed?.cpaTotal)}`);
      } catch (autoCpaErr) {
        console.error("Auto-CPA column creation failed (certificate saved without it):", autoCpaErr);
      }
    }

    setCertificateForm(makeCertificateForm(selectedProject));
    setEditingCertificateId(null);
  });

  const handleEditCertificate = useStableCallback((row) => {
    setEditingCertificateId(row.id);
    setCertificateForm({ ...makeCertificateForm(selectedProject), ...row });
  });


  const handleUpdateCertificatePayment = useStableCallback(async (row, updates = {}) => {
    if (!selectedProject || !row?.id) return;

    await setDoc(
      doc(db, "projects", selectedProject, "certificates", row.id),
      {
        ...updates,
        projectId: selectedProject,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    setCertificates((prev) =>
      (prev || []).map((item) =>
        item.id === row.id
          ? {
              ...item,
              ...updates,
            }
          : item
      )
    );

    await writeAuditLog(
      selectedProject,
      "certificate",
      row.id,
      "payment_update",
      `Updated payment status for certificate ${row.certificateNo || row.id}`,
      updates
    );
  });





  // Phase 4: engineer approval of an auto-captured certificate. Flips status and
  // records who/when, with an audit entry. Reused for revert (approved=false).
  const handleSetCertificateApproval = useStableCallback(async (row, approved) => {
    if (!selectedProject || !row?.id) return;
    const updates = {
      status: approved ? "approved" : "awaiting_approval",
      approvedAt: approved ? new Date().toISOString() : "",
      approvedBy: approved ? (auth?.currentUser?.email || auth?.currentUser?.uid || "") : "",
    };
    await setDoc(
      doc(db, "projects", selectedProject, "certificates", row.id),
      { ...updates, projectId: selectedProject, updatedAt: serverTimestamp() },
      { merge: true }
    );
    setCertificates((prev) => (prev || []).map((item) => (item.id === row.id ? { ...item, ...updates } : item)));
    await writeAuditLog(
      selectedProject,
      "certificate",
      row.id,
      approved ? "approve" : "unapprove",
      `${approved ? "Approved" : "Reverted to awaiting approval"} certificate ${row.certificateNo || row.id}`,
      updates
    );
  });

  const handleDeleteCertificate = useStableCallback(async (id) => {
    if (!selectedProject) return;
    await deleteDoc(doc(db, "projects", selectedProject, "certificates", id));
    await writeAuditLog(selectedProject, "certificate", id, "delete", `Deleted certificate ${id}`);
    if (editingCertificateId === id) {
      setCertificateForm(makeCertificateForm(selectedProject));
      setEditingCertificateId(null);
    }
  });

  const clearCertificateForm = useStableCallback(() => {
    setCertificateForm(makeCertificateForm(selectedProject));
    setEditingCertificateId(null);
  });

  const handleAddDiary = useStableCallback(async () => {
    if (!selectedProject || !(diaryForm.diaryNo || diaryForm.date)) return;

    const diaryId = editingDiaryId || diaryForm.diaryNo || `DAY-${Date.now()}`;
    const progressEnabled = String(diaryForm.progressTrackingEnabled || "No") === "Yes";
    const progressStart = Number(diaryForm.progressChainageStart);
    const progressEnd = Number(diaryForm.progressChainageEnd);
    const hasProgressInput =
      progressEnabled &&
      String(diaryForm.progressAssetId || "").trim() &&
      Number.isFinite(progressStart) &&
      Number.isFinite(progressEnd) &&
      progressEnd > progressStart;

    const payload = {
      ...diaryForm,
      id: diaryId,
      projectId: selectedProject,
      hasProgressSegment: hasProgressInput,
      progressSegmentId: hasProgressInput ? `${diaryId}_progress` : "",
      updatedAt: serverTimestamp(),
    };

    await setDoc(doc(db, "projects", selectedProject, "diaries", diaryId), payload, { merge: true });

    const progressSegmentRef = doc(db, "projects", selectedProject, "progressSegments", `${diaryId}_progress`);

    if (hasProgressInput) {
      const selectedProgressAsset = progressAssets.find((asset) => asset.id === diaryForm.progressAssetId);
      const progressAssetName = diaryForm.progressAssetName || selectedProgressAsset?.assetName || selectedProgressAsset?.name || "Pipeline";

      await setDoc(
        progressSegmentRef,
        {
          id: `${diaryId}_progress`,
          projectId: selectedProject,
          diaryId,
          diaryNo: diaryForm.diaryNo || diaryId,
          diaryDate: diaryForm.date || "",

          assetType: diaryForm.progressAssetType || selectedProgressAsset?.assetType || "pipeline",
          assetId: diaryForm.progressAssetId,
          assetName: progressAssetName,

          activity: diaryForm.progressActivity || "pipe_laying",
          chainageStart: progressStart,
          chainageEnd: progressEnd,

          status: diaryForm.progressStatus || "completed",
          quantity: progressEnd - progressStart,
          unit: "m",

          // Section properties the measurement sheet and the tracker read.
          clearanceSubType: diaryForm.progressClearanceSubType || "",
          excavationCondition: diaryForm.progressExcavationCondition || "",

          notes: diaryForm.progressNotes || diaryForm.worksPerformed || "",
          updatedAt: serverTimestamp(),
          createdAt: serverTimestamp(),
        },
        { merge: true }
      );
    } else if (editingDiaryId) {
      await deleteDoc(progressSegmentRef);
    }

    await writeAuditLog(selectedProject, "diary", diaryId, editingDiaryId ? "update" : "create", `Saved diary ${diaryId}`);
    setDiaryForm(makeDiaryForm(selectedProject));
    setEditingDiaryId(null);
  });

  const handleEditDiary = useStableCallback((row) => {
    setEditingDiaryId(row.id);
    setDiaryForm({ ...makeDiaryForm(selectedProject), ...row });
  });

  const handleDeleteDiary = useStableCallback(async (id) => {
    if (!selectedProject) return;
    await deleteDoc(doc(db, "projects", selectedProject, "diaries", id));
    await writeAuditLog(selectedProject, "diary", id, "delete", `Deleted diary ${id}`);
    if (editingDiaryId === id) {
      setDiaryForm(makeDiaryForm(selectedProject));
      setEditingDiaryId(null);
    }
  });

  const clearDiaryForm = useStableCallback(() => {
    setDiaryForm(makeDiaryForm(selectedProject));
    setEditingDiaryId(null);
  });


  const handleCopyPreviousDiary = useStableCallback(() => {
    if (!diaries.length) return;
    const latest = [...diaries].sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")))[0];
    setDiaryForm({
      ...makeDiaryForm(selectedProject),
      ...latest,
      diaryNo: "",
      date: "",
      contractorSignName: "",
      contractorSigned: "No",
      engineerSignName: "",
      engineerSigned: "No",
      isLocked: "No",
      progressTrackingEnabled: "No",
      progressAssetId: "",
      progressAssetName: "",
      progressChainageStart: "",
      progressChainageEnd: "",
      progressNotes: "",
    });
    setEditingDiaryId(null);
  });

  const handleSoqImportComplete = useStableCallback(() => {
    setActiveSection("soqRegister");
  });

  const updateBlockValue = useCallback(async (type, monthKey, field, value) => {
    if (type !== "forecast" || !selectedProject || !monthKey || !field) return;

    const nextProjectBlocks = {
      ...buildBlockMap(monthRange, forecastByProject[selectedProject] || {}),
      [monthKey]: {
        ...((forecastByProject[selectedProject] || {})[monthKey] || {
          scheduledWorks: "",
          cpa: "",
          extraWork: "",
        }),
        [field]: value,
      },
    };

    setForecastByProject((prev) => ({
      ...prev,
      [selectedProject]: nextProjectBlocks,
    }));

    await setDoc(
      doc(db, "projects", selectedProject, "cashflow", "forecast"),
      {
        projectId: selectedProject,
        blocks: nextProjectBlocks,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  }, [forecastByProject, monthRange, selectedProject]);

const handleAddAdditionalCashflowMonth = useCallback(
  async (monthKey) => {
    if (!selectedProject || !monthKey) return;

    const cleanMonthKey = String(monthKey).slice(0, 7);
    if (!cleanMonthKey) return;

    await setDoc(
      doc(db, "projects", selectedProject, "additionalCashflowMonths", cleanMonthKey),
      {
        id: cleanMonthKey,
        key: cleanMonthKey,
        label: formatMonthLabel(cleanMonthKey),
        projectId: selectedProject,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  },
  [selectedProject]
);

const handleDeleteAdditionalCashflowMonth = useCallback(
  async (monthKey) => {
    if (!selectedProject || !monthKey) return;

    const cleanMonthKey = String(monthKey).slice(0, 7);
    if (!cleanMonthKey) return;

    await deleteDoc(
      doc(db, "projects", selectedProject, "additionalCashflowMonths", cleanMonthKey)
    );
  },
  [selectedProject]
);

const handleSaveDueCompletion = async (payload = {}) => {
  if (!selectedProject) return;

  const updates = {
    ...payload,
    updatedAt: serverTimestamp(),
  };

  await setDoc(doc(db, "projects", selectedProject), updates, { merge: true });

  setProjects((prev) =>
    prev.map((item) =>
      item.id === selectedProject
        ? { ...item, ...updates }
        : item
    )
  );

  await writeAuditLog(
    selectedProject,
    "dueCompletion",
    selectedProject,
    "update",
    "Updated GCC Due Completion Date calculation",
    {
      baseDueCompletionDate: payload?.dueCompletionResult?.baseDueCompletionDate || "",
      currentDueCompletionDate: payload?.dueCompletionResult?.currentDueCompletionDate || "",
    }
  );
};



  const handleUpdateSoqItem = useCallback(
    async (itemId, updates = {}) => {
      if (!selectedProject || !itemId) return;

      await setDoc(
        doc(db, "projects", selectedProject, "soqItems", itemId),
        {
          ...updates,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    },
    [selectedProject]
  );

  const handleCreateCpaClass = useCallback(
    async (nextClass = {}) => {
      if (!selectedProject) return;

      const className = String(nextClass.name || nextClass.id || "").trim();
      if (!className) return;

      const classId = String(
        nextClass.id ||
          className
            .toLowerCase()
            .replace(/[^a-z0-9]+/gi, "_")
            .replace(/^_+|_+$/g, "")
      ).trim();

      if (!classId) return;

      const payload = {
        ...nextClass,
        id: classId,
        name: className,
        linkedCpaTableId: String(nextClass.linkedCpaTableId || "").trim(),
        projectId: selectedProject,
        active: nextClass.active !== false,
        updatedAt: serverTimestamp(),
      };

      await setDoc(
        doc(db, "projects", selectedProject, "cpaClasses", classId),
        payload,
        { merge: true }
      );

      await writeAuditLog(
        selectedProject,
        "cpaClass",
        classId,
        "create_or_update",
        `Saved CPA class ${className}`,
        {
          name: className,
          linkedCpaTableId: payload.linkedCpaTableId,
        }
      );
    },
    [selectedProject]
  );

  const handleDeleteCpaClass = useCallback(
    async (classId) => {
      if (!selectedProject || !classId) return;

      await deleteDoc(doc(db, "projects", selectedProject, "cpaClasses", classId));

      await writeAuditLog(
        selectedProject,
        "cpaClass",
        classId,
        "delete",
        `Deleted CPA class ${classId}`,
        { classId }
      );
    },
    [selectedProject]
  );

const handleSetCpaColumns = useCallback(
  async (updater) => {
    if (!selectedProject) return;

    const nextColumns = typeof updater === "function" ? updater(cpaColumns) : updater;
    setCpaColumns(nextColumns || []);

    const existingSnap = await getDocs(collection(db, "projects", selectedProject, "cpaColumns"));
    const nextIds = new Set((nextColumns || []).map((item) => item.id));

    await Promise.all(
      existingSnap.docs
        .filter((item) => !nextIds.has(item.id))
        .map((item) => deleteDoc(doc(db, "projects", selectedProject, "cpaColumns", item.id)))
    );

    await Promise.all(
      (nextColumns || []).map((item) =>
        setDoc(
          doc(db, "projects", selectedProject, "cpaColumns", item.id),
          {
            ...item,
            projectId: selectedProject,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        )
      )
    );
  },
  [cpaColumns, selectedProject]
);

const handleSetFidicCpaTables = useCallback(
  async (updater) => {
    if (!selectedProject) return;

    const nextTables = typeof updater === "function" ? updater(fidicCpaTables) : updater;
    const safeTables = nextTables || createDefaultFidicCpaState();

    setFidicCpaTables(safeTables);

    await setDoc(
      doc(db, "projects", selectedProject, "cpa", "fidicTables"),
      {
        projectId: selectedProject,
        tables: safeTables,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  },
  [fidicCpaTables, selectedProject]
);

const handleSetRoeRows = useCallback(
  async (updater) => {
    if (!selectedProject) return;

    const nextRows = typeof updater === "function" ? updater(roeRows) : updater;
    const safeRows = Array.isArray(nextRows) ? nextRows : [];
    setRoeRows(safeRows);

    const existingSnap = await getDocs(collection(db, "projects", selectedProject, "roeRows"));
    const nextIds = new Set(safeRows.map((item) => item.id).filter(Boolean));

    await Promise.all(
      existingSnap.docs
        .filter((item) => !nextIds.has(item.id))
        .map((item) => deleteDoc(doc(db, "projects", selectedProject, "roeRows", item.id)))
    );

    await Promise.all(
      safeRows.map((item) => {
        const rowId = item.id || `roe-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        return setDoc(
          doc(db, "projects", selectedProject, "roeRows", rowId),
          {
            ...item,
            id: rowId,
            projectId: selectedProject,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      })
    );
  },
  [roeRows, selectedProject]
);

const computedCpaColumns = useMemo(() => {
  const sharedGccBaseIndexSets = (cpaColumns || []).find(
    (column) => Array.isArray(column.baseIndexSets) && column.baseIndexSets.length
  )?.baseIndexSets;
  const ordered = [...(cpaColumns || [])].sort((a, b) =>
    String(a.certificateMonth || "").localeCompare(String(b.certificateMonth || ""))
  ).map((column) => ({
    ...column,
    baseIndexSets:
      Array.isArray(column.baseIndexSets) && column.baseIndexSets.length
        ? column.baseIndexSets
        : sharedGccBaseIndexSets,
  }));

  return ordered.reduce((acc, column) => {
    const previousComputed = acc.length ? acc[acc.length - 1].computed : null;
    const computed = calculateCpaColumnForApp(column, previousComputed);
    acc.push({ ...column, computed });
    return acc;
  }, []);
}, [cpaColumns]);

const currentContractType = currentProject?.contractType || projectForm?.contractType || "GCC";
const currentCpaType = normaliseCpaType(
  currentProject?.cpaType ||
    projectForm?.cpaType ||
    currentProject?.contractType ||
    projectForm?.contractType
);

const fidicCpaTotals = useMemo(
  () => calculateFidicCpaTotals(fidicCpaTables),
  [fidicCpaTables]
);

const latestCpaCumulative = calculateLatestCpaForContract(
  currentCpaType,
  computedCpaColumns,
  fidicCpaTables
);

const latestCpaIndexStatus = getLatestCpaIndexStatusForApp(
  currentCpaType,
  computedCpaColumns,
  fidicCpaTables
);

const fidicCpaByCertificateNo = useMemo(() => {
  return buildFidicCpaByCertificateNo(fidicCpaTables);
}, [fidicCpaTables]);

  const certificatesWithCpa = useMemo(() => {
    const isFidicContract = currentCpaType === "FIDIC";
    const orderedCertificates = [...(certificates || [])].sort((a, b) => {
      const dateCompare = String(a.certificateDate || "").localeCompare(String(b.certificateDate || ""));
      if (dateCompare !== 0) return dateCompare;
      return String(a.certificateNo || "").localeCompare(String(b.certificateNo || ""), undefined, {
        numeric: true,
        sensitivity: "base",
      });
    });

    let runningCpa = 0;
    let previousGccLiveCpaCumulative = 0;

    return orderedCertificates.map((certificate) => {
      const certificateNo = String(certificate.certificateNo || certificate.id || "").trim();
      const monthKey = toMonthKey(certificate.certificateMonth || certificate.certificateDate);
      const roeThisCertificate = getRoeThisClaimForCertificate(roeRows, certificateNo);
      const roeThisMonth = getRoeThisClaimForMonth(roeRows, monthKey);
      const roeAmount = roeThisCertificate || roeThisMonth;

      if (!isFidicContract) {
        const matchingCpaColumn = (computedCpaColumns || []).find(
          (item) =>
            String(item.certificateNo || "") === certificateNo ||
            String(item.certificateMonth || "") === String(certificate.certificateMonth || "")
        );
        const currentCpaCumulative = numberOrZero(matchingCpaColumn?.computed?.totalCumulativeCpa);
        const currentCpaThisCertificate = numberOrZero(
          matchingCpaColumn?.computed?.cpaTotal ?? currentCpaCumulative - previousGccLiveCpaCumulative
        );
        previousGccLiveCpaCumulative = currentCpaCumulative;

        return {
          ...certificate,
          currentCpaCumulative,
          liveCpaCumulative: currentCpaCumulative,
          currentCpaThisCertificate,
          liveCpaThisCertificate: currentCpaThisCertificate,
          currentCpaCertifiedValue: numberOrZero(matchingCpaColumn?.computed?.certifiedValueIncludingCpa),
          roeThisCertificate: roeAmount,
        };
      }

      const cpaMatch = (() => {
        if (!fidicCpaByCertificateNo) return null;
        // Try exact, then numeric comparison to handle "001" vs "1" mismatches
        if (fidicCpaByCertificateNo[certificateNo]) return fidicCpaByCertificateNo[certificateNo];
        const n = parseFloat(certificateNo.replace(/[^0-9.]/g, ""));
        if (!isNaN(n)) {
          return Object.values(fidicCpaByCertificateNo).find((v) =>
            parseFloat(String(v.certificateNo || "").replace(/[^0-9.]/g, "")) === n
          ) || null;
        }
        return null;
      })();
      const currentFidicCpaThisCertificate = numberOrZero(cpaMatch?.cpaThisCertificate);
      runningCpa += currentFidicCpaThisCertificate;

      return {
        ...certificate,
        currentCpaCumulative: runningCpa,
        liveCpaCumulative: runningCpa,
        currentCpaThisCertificate: currentFidicCpaThisCertificate,
        liveCpaThisCertificate: currentFidicCpaThisCertificate,
        currentCpaCertifiedValue: numberOrZero(cpaMatch?.cpaCertifiedValue),
        roeThisCertificate: roeAmount,
      };
    });
  }, [certificates, currentCpaType, computedCpaColumns, fidicCpaByCertificateNo, roeRows]);

  const activeActualBlocks = useMemo(() => {
    const monthTotals = {};

    const orderedCertificates = [...(certificatesWithCpa || [])].sort((a, b) => {
      const dateCompare = String(a.certificateDate || "").localeCompare(String(b.certificateDate || ""));
      if (dateCompare !== 0) return dateCompare;
      return String(a.certificateNo || "").localeCompare(String(b.certificateNo || ""), undefined, {
        numeric: true,
        sensitivity: "base",
      });
    });

    let previousSubtotalA = 0;
    let previousCpaCumulative = 0;

    orderedCertificates.forEach((item) => {
      const monthKey = toMonthKey(item.certificateMonth || item.certificateDate);
      if (!monthKey) return;

      const claimedItemsTotal = sumCertificateClaimedItemsForCashflow(item);
      const extraWorkTotal = sumCertificateExtraWorkItems(item);
      const hasCertificateBuildUpItems =
        (Array.isArray(item?.claimedItems) && item.claimedItems.length > 0) ||
        (Array.isArray(item?.extraWorkItems) && item.extraWorkItems.length > 0);

      const storedSubtotalA = getCertificateSubtotalAForCashflow(item);
      const subtotalA = hasCertificateBuildUpItems
        ? numberOrZero(previousSubtotalA) + numberOrZero(claimedItemsTotal) + numberOrZero(extraWorkTotal)
        : storedSubtotalA;

      const subtotalADifference = subtotalA - previousSubtotalA;
      const scheduledWorksA = hasCertificateBuildUpItems
        ? claimedItemsTotal
        : getCertificateScheduledWorksAForCashflow(item, subtotalADifference - extraWorkTotal);
      const cpaInitial = getCertificateCpaInitialForCashflow(item, previousCpaCumulative);
      const scheduledWorksPlusCpaInitialC = scheduledWorksA + cpaInitial;

      if (!monthTotals[monthKey]) {
        monthTotals[monthKey] = {
          scheduledWorks: 0,
          cpa: 0,
          extraWork: 0,
          total: 0,
          actualMonthlyTotal: 0,
          actualTotalExCpa: 0,
          actualTotalIncCpa: 0,
          subtotalA: 0,
          previousSubtotalA: 0,
        };
      }

      monthTotals[monthKey].scheduledWorks += scheduledWorksA;
      monthTotals[monthKey].cpa += cpaInitial;
      monthTotals[monthKey].extraWork += extraWorkTotal;
      monthTotals[monthKey].total += scheduledWorksPlusCpaInitialC + extraWorkTotal;
      monthTotals[monthKey].actualMonthlyTotal += scheduledWorksPlusCpaInitialC + extraWorkTotal;
      monthTotals[monthKey].actualTotalExCpa += scheduledWorksA + extraWorkTotal;
      monthTotals[monthKey].actualTotalIncCpa += scheduledWorksPlusCpaInitialC + extraWorkTotal;
      monthTotals[monthKey].subtotalA = subtotalA;
      monthTotals[monthKey].previousSubtotalA = previousSubtotalA;

      previousSubtotalA = subtotalA;
      previousCpaCumulative = numberOrZero(getCertificateCpaCumulativeForCashflow(item));
    });

    const cashflowMonthKeys = Array.from(
      new Set([
        ...(monthRange || []),
        ...Object.keys(monthTotals),
        ...(additionalCashflowMonths || []).map((month) =>
          typeof month === "string" ? month : month.key || month.id
        ),
      ])
    ).filter(Boolean);

    return buildBlockMap(
      cashflowMonthKeys,
      Object.keys(monthTotals).reduce((acc, monthKey) => {
        acc[monthKey] = {
          scheduledWorks: monthTotals[monthKey].scheduledWorks,
          cpa: monthTotals[monthKey].cpa,
          extraWork: monthTotals[monthKey].extraWork,
          total: monthTotals[monthKey].total,
          actualMonthlyTotal: monthTotals[monthKey].actualMonthlyTotal,

          // A/B/C actual certificate values for CashflowSection
          scheduledWorksA: monthTotals[monthKey].scheduledWorks,
          actualScheduledWorksA: monthTotals[monthKey].scheduledWorks,
          actualScheduledA: monthTotals[monthKey].scheduledWorks,
          cpaInitialB: monthTotals[monthKey].cpa,
          actualCpaInitialB: monthTotals[monthKey].cpa,
          scheduledWorksPlusCpaInitialC: monthTotals[monthKey].scheduledWorks + monthTotals[monthKey].cpa,
          actualScheduledWorksPlusCpaInitialC: monthTotals[monthKey].scheduledWorks + monthTotals[monthKey].cpa,
          actualC: monthTotals[monthKey].scheduledWorks + monthTotals[monthKey].cpa,

          actualTotalExCpa: monthTotals[monthKey].actualTotalExCpa,
          actualTotalIncCpa: monthTotals[monthKey].actualTotalIncCpa,
          totalExCpa: monthTotals[monthKey].actualTotalExCpa,
          totalIncCpa: monthTotals[monthKey].actualTotalIncCpa,
          subtotalA: monthTotals[monthKey].subtotalA,
          previousSubtotalA: monthTotals[monthKey].previousSubtotalA,
        };
        return acc;
      }, {})
    );
  }, [certificatesWithCpa, monthRange, additionalCashflowMonths]);

  const omissionTotalsBySection = variations
    .filter((item) => item.projectId === selectedProject)
    .filter((item) => isApprovedVariation(item))
    .filter((item) => isOmissionVariation(item))
    .reduce((totals, item) => {
      const preferredKey = normalizeBoqValue(item.boqSectionId)
        || normalizeBoqValue(item.boqSectionReference)
        || normalizeBoqValue(item.boqSectionLabel)
        || normalizeBoqValue(item.boqSectionNo)
        || normalizeBoqValue(item.boqSectionName)
        || normalizeBoqValue(item.boqSection)
        || normalizeBoqValue(item.sectionNo)
        || normalizeBoqValue(item.sectionName);

      if (!preferredKey) return totals;

      const omissionAmount = getVariationOmissionAmount(item);
      if (!omissionAmount) return totals;

      totals[preferredKey] = (totals[preferredKey] || 0) + omissionAmount;
      return totals;
    }, {});

  const boqRows = Array.isArray(boqSections)
    ? boqSections.map((item) => {
        const tenderedAmount = numberOrZero(
          item.tenderedAmount ??
            item.tenderAmount ??
            item.contractValue ??
            item.sectionTotal ??
            item.total ??
            item.amount ??
            0
        );
        const engineersEstimate = numberOrZero(item.engineersEstimate ?? item.engineerEstimate ?? tenderedAmount);
        const sectionKeys = buildPossibleBoqKeys(
          item.id,
          `${item.sectionNo || ""} ${item.sectionName || ""}`,
          `${item.sectionNo || ""} - ${item.sectionName || ""}`,
          item.sectionNo,
          item.sectionName,
          `${item.sectionName || ""} ${item.sectionNo || ""}`
        );
        const omissionAmount = sectionKeys.reduce((found, key) => {
          if (found > 0) return found;
          return numberOrZero(omissionTotalsBySection[key]);
        }, 0);
        const revisedAmount = engineersEstimate - omissionAmount;

        return {
          ...item,
          tenderedAmount,
          engineersEstimate,
          omissionAmount,
          revisedAmount,
        };
      })
    : [];

  const boqTenderedTotal = boqRows.reduce((sum, item) => sum + numberOrZero(item.tenderedAmount), 0);
  const boqRevisedTotal = boqRows.reduce((sum, item) => sum + numberOrZero(item.revisedAmount), 0);
  const approvedVariationTotal = variations
    .filter((item) => item.projectId === selectedProject)
    .filter((item) => isApprovedVariation(item))
    .filter((item) => !isOmissionVariation(item))
    .reduce(
      (sum, item) =>
        sum +
        numberOrZero(
          item.approvedValue ??
            item.netVoAmount ??
            item.assessedValue ??
            item.additionalWorkAmount ??
            0
        ),
      0
    );
  const totalClaimCost = claims
    .filter((item) => item.projectId === selectedProject)
    .reduce((sum, item) => sum + numberOrZero(item.pgClaimed), 0);
  const engineersEstimateTotal = boqRows.reduce((sum, item) => sum + numberOrZero(item.engineersEstimate), 0);
  const claimsDashboard = {
    totalClaims: claims.length,
    inReviewClaims: claims.filter((item) => String(item.status || "") === "In Review").length,
    lateSubmissions: claims.filter((item) => String(item.status || "") === "Time Barred").length,
    atRiskClaims: claims.filter((item) => String(item.status || "") === "In Review").length,
  };

  // ── Auth loading splash ───────────────────────────────────────────────────
  if (authLoading) {
    return (
      <div style={{ minHeight: "100vh", background: "#0f172a", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center", color: "#93c5fd" }}>
          <img src={`${process.env.PUBLIC_URL}/logo-mark.png`} alt="Soloforce Celestia" style={{ width: 64, height: 64, marginBottom: 12, borderRadius: 14 }} />
          <div style={{ fontSize: 13, color: "#64748b", letterSpacing: "0.1em" }}>Loading…</div>
        </div>
      </div>
    );
  }

  // ── Demo provisioning / error screen ──────────────────────────────────────
  if (demoState.status === "provisioning" || demoState.status === "error") {
    const failed = demoState.status === "error";
    return (
      <div style={{ minHeight: "100vh", background: "#0f172a", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Inter, sans-serif", padding: 24 }}>
        <div style={{ textAlign: "center", color: "#e2e8f0", maxWidth: 460 }}>
          <img src={`${process.env.PUBLIC_URL}/logo-mark.png`} alt="Soloforce Celestia" style={{ width: 64, height: 64, marginBottom: 18, borderRadius: 14 }} />
          {failed ? (
            <>
              <h2 style={{ margin: "0 0 8px", fontSize: 20, fontWeight: 800 }}>We couldn't start the demo</h2>
              <p style={{ color: "#94a3b8", fontSize: 14, lineHeight: 1.6, margin: "0 0 22px" }}>{demoState.error}</p>
              <button onClick={startDemo} style={{ background: "linear-gradient(135deg,#2563eb,#1d4ed8)", color: "#fff", border: "none", borderRadius: 10, padding: "11px 22px", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>Try again</button>
            </>
          ) : (
            <>
              <div style={{ width: 34, height: 34, margin: "0 auto 18px", border: "3px solid rgba(148,163,184,0.25)", borderTopColor: "#60a5fa", borderRadius: "50%", animation: "demoSpin 0.8s linear infinite" }} />
              <h2 style={{ margin: "0 0 8px", fontSize: 20, fontWeight: 800 }}>Setting up your private demo…</h2>
              <p style={{ color: "#94a3b8", fontSize: 14, lineHeight: 1.6, margin: 0 }}>We're cloning a fresh sample project just for you. This only takes a few seconds.</p>
            </>
          )}
        </div>
        <style>{"@keyframes demoSpin{to{transform:rotate(360deg)}}"}</style>
      </div>
    );
  }

  // ── Login screen ─────────────────────────────────────────────────────────
  if (!currentUser) {
    return (
      <div style={{ minHeight: "100vh", background: "#0f172a", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Inter, sans-serif" }}>
        <div style={{ background: "#1e293b", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 20, padding: "40px 36px", width: "100%", maxWidth: 400, boxShadow: "0 24px 64px rgba(0,0,0,0.5)" }}>
          <div style={{ textAlign: "center", marginBottom: 32 }}>
            <img
              src={`${process.env.PUBLIC_URL}/brand-lockup.png`}
              alt="Soloforce Celestia"
              style={{ width: "100%", maxWidth: 300, height: "auto", display: "block", margin: "0 auto 18px", borderRadius: 12, boxShadow: "0 8px 24px rgba(0,0,0,0.35)" }}
            />
            <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: 13 }}>Sign in to your account</p>
          </div>
          <form onSubmit={handleLogin} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#94a3b8", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.06em" }}>Email</label>
              <input
                type="email" required autoComplete="email"
                value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)}
                style={{ width: "100%", background: "#0f172a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, padding: "10px 14px", color: "#f8fafc", fontSize: 14, outline: "none", boxSizing: "border-box" }}
                placeholder="you@organisation.com"
              />
            </div>
            <div>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#94a3b8", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.06em" }}>Password</label>
              <input
                type="password" required autoComplete="current-password"
                value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)}
                style={{ width: "100%", background: "#0f172a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, padding: "10px 14px", color: "#f8fafc", fontSize: 14, outline: "none", boxSizing: "border-box" }}
                placeholder="••••••••"
              />
            </div>
            {loginError && (
              <div style={{ background: "rgba(220,38,38,0.12)", border: "1px solid rgba(220,38,38,0.3)", borderRadius: 8, padding: "10px 14px", color: "#f87171", fontSize: 13 }}>{loginError}</div>
            )}
            <button
              type="submit" disabled={loginLoading}
              style={{ background: "linear-gradient(135deg,#2563eb,#1d4ed8)", color: "#fff", border: "none", borderRadius: 10, padding: "12px", fontSize: 14, fontWeight: 700, cursor: loginLoading ? "not-allowed" : "pointer", opacity: loginLoading ? 0.7 : 1, marginTop: 4 }}
            >
              {loginLoading ? "Signing in…" : "Sign In"}
            </button>
          </form>
          <div style={{ marginTop: 18, paddingTop: 18, borderTop: "1px solid rgba(255,255,255,0.06)", textAlign: "center" }}>
            <button
              type="button" onClick={startDemo}
              style={{ width: "100%", background: "transparent", color: "#93c5fd", border: "1px solid rgba(96,165,250,0.40)", borderRadius: 10, padding: "11px", fontSize: 14, fontWeight: 700, cursor: "pointer" }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(96,165,250,0.10)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
            >
              Try the live demo
            </button>
            <p style={{ color: "#475569", fontSize: 11.5, margin: "10px 0 0" }}>No sign-up — explore a private sample project.</p>
          </div>
          <p style={{ textAlign: "center", color: "#475569", fontSize: 12, marginTop: 20, marginBottom: 0 }}>
            Contact your administrator to create or reset your account.
          </p>
          <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid rgba(255,255,255,0.06)" }}>
            <p style={{ textAlign: "center", color: "#475569", fontSize: 10.5, lineHeight: 1.5, margin: 0 }}>
              {LICENCE_SUMMARY}
            </p>
            <p style={{ textAlign: "center", color: "#334155", fontSize: 10.5, marginTop: 10, marginBottom: 0 }}>
              {COPYRIGHT}
            </p>
            <p style={{ textAlign: "center", color: "#334155", fontSize: 10.5, marginTop: 6, marginBottom: 0, fontVariantNumeric: "tabular-nums" }}>
              Build v{BUILD_INFO.version} · {BUILD_INFO.commit} · {BUILD_INFO.date}
            </p>
          </div>
        </div>
      </div>
    );
  }

// The demo banner sits above whichever app shell renders (mobile or desktop).
// It only appears for anonymous (demo) visitors.
const demoBtnSolid = { display: "inline-block", background: "#fff", color: "#1d4ed8", textDecoration: "none", border: "none", borderRadius: 7, padding: "5px 12px", fontSize: 12.5, fontWeight: 700, cursor: "pointer" };
const demoBtnGhost = { display: "inline-block", background: "rgba(255,255,255,0.14)", color: "#fff", textDecoration: "none", border: "1px solid rgba(255,255,255,0.35)", borderRadius: 7, padding: "5px 12px", fontSize: 12.5, fontWeight: 600, cursor: "pointer" };
const demoBanner = isAnonDemo ? (
  <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", justifyContent: "center", background: "linear-gradient(90deg,#1d4ed8,#7c3aed)", color: "#fff", padding: "9px 16px", fontSize: 13, fontFamily: "Inter, sans-serif", zIndex: 50 }}>
    <span style={{ fontWeight: 700 }}>You're exploring a private demo of Celestia</span>
    <span style={{ opacity: 0.85 }}>Sample data{demoDaysLeft != null ? ` · expires in ${demoDaysLeft} day${demoDaysLeft === 1 ? "" : "s"}` : ""}</span>
    <button onClick={resetDemoData} style={demoBtnGhost}>Reset demo</button>
    <a href={`${MARKETING_URL}/#pricing`} target="_blank" rel="noreferrer" style={demoBtnSolid}>Get full access</a>
    <a href={`${MARKETING_URL}/#contact`} target="_blank" rel="noreferrer" style={demoBtnGhost}>Contact us</a>
  </div>
) : null;

// Inside your render function, before the existing return:
if (window.innerWidth <= 768) return (
  <>
  {demoBanner}
  <MobileApp
    projects={projects}
    selectedProject={selectedProject}
    setSelectedProject={setSelectedProject}
    currentProject={currentProject}
    activeSection={activeSection}
    setActiveSection={setActiveSection}
    rfis={rfis} rfiForm={rfiForm} setRfiForm={setRfiForm}
    handleAddRfi={handleAddRfi} handleEditRfi={handleEditRfi}
    handleDeleteRfi={handleDeleteRfi} editingRfiId={editingRfiId}
    clearRfiForm={clearRfiForm} variations={variations}
    diaries={diaries} diaryForm={diaryForm} setDiaryForm={setDiaryForm}
    handleAddDiary={handleAddDiary} handleEditDiary={handleEditDiary}
    handleDeleteDiary={handleDeleteDiary} editingDiaryId={editingDiaryId}
    clearDiaryForm={clearDiaryForm} handleCopyPreviousDiary={handleCopyPreviousDiary}
    auditLogs={auditLogs} handleViewItem={handleViewItem}
    currentUser={currentUser} handleSignOut={handleSignOut}
    isAdmin={userClaims.admin}
ncrs={ncrs} ncrForm={ncrForm} setNcrForm={setNcrForm}
handleAddOrUpdateNcr={handleAddOrUpdateNcr} handleEditNcr={handleEditNcr}
handleDeleteNcr={handleDeleteNcr} editingNcrId={editingNcrId} clearNcrForm={clearNcrForm}
claims={claims} claimForm={claimForm} setClaimForm={setClaimForm}
handleAddOrUpdateClaim={handleAddOrUpdateClaim} handleEditClaim={handleEditClaim}
handleDeleteClaim={handleDeleteClaim} editingClaimId={editingClaimId} clearClaimForm={clearClaimForm}
instructions={instructions} instructionForm={instructionForm} setInstructionForm={setInstructionForm}
handleAddInstruction={handleAddInstruction} handleEditInstruction={handleEditInstruction}
handleDeleteInstruction={handleDeleteInstruction} editingInstructionId={editingInstructionId}
clearInstructionForm={clearInstructionForm} handleCreateVariationFromInstruction={handleCreateVariationFromInstruction}
variationForm={variationForm} setVariationForm={setVariationForm}
handleAddOrUpdateVariation={handleAddOrUpdateVariation} handleEditVariation={handleEditVariation}
handleDeleteVariation={handleDeleteVariation} editingVariationId={editingVariationId}
certificates={certificates} soqItems={soqItems} boqRows={boqRows}
  />
  </>
);


  return (
    <div style={APP_STYLES.shell}>
      {demoBanner}
      {/* ── Top Bar ── */}
      <header style={APP_STYLES.topBar}>
        <div style={APP_STYLES.topBarLeft}>
          <button
            style={APP_STYLES.homeButton}
            onClick={() => setActiveSection("home")}
            onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.07)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
            title="Go to Home"
          >
            <div style={{ ...APP_STYLES.homeLogo, background: "#FFFFFF", boxShadow: "0 4px 14px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.5)", overflow: "hidden", padding: 0 }}>
              <img src={`${process.env.PUBLIC_URL}/logo-mark.png`} alt="Soloforce Celestia" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </div>
            <span style={APP_STYLES.homeLabel}>Celestia</span>
          </button>
          <div style={APP_STYLES.topBarDivider} />
          <select
            value={selectedProject || ""}
            onChange={(e) => setSelectedProject(e.target.value)}
            style={APP_STYLES.projectSelect}
          >
            <option value="">Select Project</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.id}{project.name ? ` — ${project.name}` : ""}
              </option>
            ))}
          </select>
        </div>

        <div style={APP_STYLES.topBarCenter}>
          <span style={APP_STYLES.activeSectionPill}>{currentSectionLabel}</span>
        </div>

        <div style={APP_STYLES.topBarRight}>
          <SectionNavigator activeSection={activeSection} onNavigate={setActiveSection} isAdmin={userClaims.admin} />
          {currentUser && (
            <div style={{
              display: "flex", alignItems: "center", gap: 12,
              marginLeft: 6, paddingLeft: 14,
              borderLeft: "1px solid rgba(255,255,255,0.08)",
            }}>
              {/* User avatar — initials in a refined circular chip */}
              <div style={{
                width: 30, height: 30, borderRadius: "50%",
                background: "linear-gradient(135deg, #3B6FA0 0%, #1B3A57 100%)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 11, fontWeight: 700,
                color: "#FAFAF7",
                fontFamily: '"Geist", "Inter Tight", sans-serif',
                letterSpacing: "-0.02em",
                border: "1px solid rgba(255,255,255,0.12)",
                boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                flexShrink: 0,
              }} title={currentUser.email}>
                {(currentUser.displayName || currentUser.email || "?")
                  .split(/[\s@.]/).filter(Boolean).slice(0, 2)
                  .map(s => s[0]?.toUpperCase()).join("")}
              </div>
              <button
                onClick={handleSignOut}
                style={{
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.10)",
                  borderRadius: 8,
                  padding: "7px 12px",
                  color: "#CBD5E1",
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: "0.02em",
                  cursor: "pointer",
                  fontFamily: '"Inter", sans-serif',
                  transition: "all 0.18s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(199,59,42,0.18)";
                  e.currentTarget.style.borderColor = "rgba(199,59,42,0.4)";
                  e.currentTarget.style.color = "#FAFAF7";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(255,255,255,0.05)";
                  e.currentTarget.style.borderColor = "rgba(255,255,255,0.10)";
                  e.currentTarget.style.color = "#CBD5E1";
                }}
                title="Sign out"
              >
                Sign Out
              </button>
            </div>
          )}
        </div>
      </header>

      {/* ── Main Content ── */}
      <main style={APP_STYLES.content}>
        <section style={APP_STYLES.headerCard}>
          {/* Signature blueprint stripe — orange → navy across the top */}
          <div style={{
            position: "absolute", top: 0, left: 0, right: 0, height: 3,
            background: "linear-gradient(90deg, #FF6B35 0%, #1B3A57 40%, #1B3A57 100%)",
          }} />
          <div style={{ minWidth: 0, flex: 1 }}>
            {/* Eyebrow row — project ID in mono, section label in caps */}
            <div style={{
              display: "flex", alignItems: "center", gap: 10,
              marginBottom: 8, flexWrap: "wrap",
            }}>
              {currentProject?.id && (
                <span style={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#5D6470",
                  letterSpacing: "0.02em",
                }}>{currentProject.id}</span>
              )}
              {currentProject?.id && (
                <span style={{ width: 3, height: 3, borderRadius: "50%", background: "#C8C3B5" }} />
              )}
              <span style={{
                fontSize: 10,
                fontWeight: 700,
                color: "#FF6B35",
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                fontFamily: '"Inter", sans-serif',
              }}>{currentSectionLabel}</span>
            </div>

            <h2 style={APP_STYLES.headerTitle}>
              {currentProject?.name || "Project Management Dashboard"}
            </h2>

            {!currentProject?.name && (
              <p style={APP_STYLES.headerSubtitle}>
                Select a project from the top bar to begin. All commercial,
                technical and administrative records sync to one control centre.
              </p>
            )}
          </div>

          {/* Engineering data chips — each shows label + tabular number */}
          <div style={APP_STYLES.headerBadgeWrap}>
            {[
              { label: "Variations", value: variations.length },
              { label: "RFIs",       value: rfis.length },
              { label: "SI",         value: instructions.length },
              { label: "Claims",     value: claims.length },
              { label: "NCRs",       value: ncrs.length },
            ].map((chip) => (
              <span key={chip.label} style={APP_STYLES.badge}>
                <span style={{
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                  color: "#8A8B85",
                  textTransform: "uppercase",
                }}>{chip.label}</span>
                <span style={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: 14,
                  fontWeight: 700,
                  color: "#0E1116",
                  letterSpacing: "-0.01em",
                }}>{chip.value}</span>
              </span>
            ))}
            {/* CPA gets accent treatment — it's the money number */}
            <span style={{
              ...APP_STYLES.badge,
              background: "#FFF1EA",
              border: "1px solid #F5BFA3",
            }}>
              <span style={{
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: "0.1em",
                color: "#C45C1A",
                textTransform: "uppercase",
              }}>CPA</span>
              <span style={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: 14,
                fontWeight: 700,
                color: "#0E1116",
                letterSpacing: "-0.01em",
              }}>
                {latestCpaCumulative.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </span>
            </span>
          </div>
        </section>

        <section style={APP_STYLES.sectionCard}>
          {activeSection === "home" && (
            <HomeSection
              currentProject={currentProject || {}}
              projectForm={projectForm}
              setProjectForm={setProjectForm}
              isEditingProject={isEditingProject}
              isAddingProject={isAddingProject}
              beginEditProject={beginEditProject}
              saveProject={saveProject}
              cancelProjectEdit={cancelProjectEdit}
	              goToSection={setActiveSection}
	              boqSections={boqSections}
	              claims={claims}
	            />
          )}

          {activeSection === "cashflow" && (
            <CashflowSection
              currentProject={currentProject}
              monthRange={monthRange}
              activeForecastBlocks={activeForecastBlocks}
              activeActualBlocks={activeActualBlocks}
              updateBlockValue={updateBlockValue}
              latestCpaCumulative={latestCpaCumulative}
              currentContractType={currentCpaType}
              additionalCashflowMonths={additionalCashflowMonths}
              handleAddAdditionalCashflowMonth={handleAddAdditionalCashflowMonth}
              handleDeleteAdditionalCashflowMonth={handleDeleteAdditionalCashflowMonth}
            />
          )}

          {activeSection === "certificates" && (
            <CertificatesSection
              certificateForm={certificateForm}
              setCertificateForm={setCertificateForm}
              handleAddOrUpdateCertificate={handleAddOrUpdateCertificate}
              certificates={certificatesWithCpa}
              currentProjectId={selectedProject}
              editingCertificateId={editingCertificateId}
              handleEditCertificate={handleEditCertificate}
              handleUpdateCertificatePayment={handleUpdateCertificatePayment}
              handleSetCertificateApproval={handleSetCertificateApproval}
              handleDeleteCertificate={handleDeleteCertificate}
              clearCertificateForm={clearCertificateForm}
              handleViewItem={handleViewItem}
              projectDetails={currentProject || projectForm || {}}
              availableMonths={monthRange.map((month) => ({ value: month, label: formatMonthLabel(month) }))}
              latestCpaCumulative={latestCpaCumulative}
              latestCpaIndexStatus={latestCpaIndexStatus}
              currentContractType={currentContractType}
              soqItems={soqItems}
              variations={variations}
              claims={claims}
              roeRows={roeRows}
              getRoeThisClaimForCertificate={getRoeThisClaimForCertificate}
              getRoeThisClaimForMonth={getRoeThisClaimForMonth}
              getRoeCumulativeUpToCertificate={getRoeCumulativeUpToCertificate}
              cpaByCertificateNo={fidicCpaByCertificateNo}
            />
          )}

          {activeSection === "rfis" && (
            <RFISection
              rfiForm={rfiForm}
              setRfiForm={setRfiForm}
              handleAddRfi={handleAddRfi}
              rfis={rfis}
              currentProjectId={selectedProject}
              handleEditRfi={handleEditRfi}
              handleDeleteRfi={handleDeleteRfi}
              editingRfiId={editingRfiId}
              clearRfiForm={clearRfiForm}
              variations={variations}
              handleCreateVariationFromRfi={handleCreateVariationFromRfi}
              handleViewItem={handleViewItem}
            />
          )}

          {activeSection === "instructions" && (
            <InstructionsSection
              instructionForm={instructionForm}
              setInstructionForm={setInstructionForm}
              handleAddInstruction={handleAddInstruction}
              instructions={instructions}
              currentProjectId={currentProject?.id || selectedProject}
              currentProject={currentProject}
              rfis={rfis}
              variations={variations}
              editingInstructionId={editingInstructionId}
              handleEditInstruction={handleEditInstruction}
              handleDeleteInstruction={handleDeleteInstruction}
              clearInstructionForm={clearInstructionForm}
              handleCreateVariationFromInstruction={handleCreateVariationFromInstruction}
              handleViewItem={handleViewItem}
            />
          )}

          {activeSection === "projectTracker" && (
            <ProjectTracker
              currentProjectId={selectedProject}
              currentProject={currentProject}
              rfis={rfis}
              variations={variations}
              claims={claims}
              onCreateRfi={handleCreateRfiFromProjectTracker}
            />
          )}

          {activeSection === "claims" && (
            <ClaimsSection
              claimForm={claimForm}
              setClaimForm={setClaimForm}
              handleAddOrUpdateClaim={handleAddOrUpdateClaim}
              claims={claims}
              currentProjectId={selectedProject}
              editingClaimId={editingClaimId}
              handleEditClaim={handleEditClaim}
              handleDeleteClaim={handleDeleteClaim}
              clearClaimForm={clearClaimForm}
              handleViewItem={handleViewItem}
              currentProject={currentProject}
              certificates={certificatesWithCpa}
            />
          )}

          {activeSection === "claude" && (
            <ClaudeSection
              currentProject={currentProject}
              currentProjectId={selectedProject}
              claims={claims}
              variations={variations}
              rfis={rfis}
              instructions={instructions}
              certificates={certificatesWithCpa}
            />
          )}

          {activeSection === "variations" && (
            <VariationsSection
              projects={projects}
              variationForm={variationForm}
              setVariationForm={setVariationForm}
              handleAddOrUpdateVariation={handleAddOrUpdateVariation}
              editingVariationId={editingVariationId}
              variations={variations}
              handleEditVariation={handleEditVariation}
              handleDeleteVariation={handleDeleteVariation}
              handleBulkUpdateVariations={handleBulkUpdateVariations}
              rfis={rfis}
              boqSections={boqSections}
              soqItems={soqItems}
              handleViewItem={handleViewItem}
              certificates={certificates}
              currentProjectId={currentProject?.id || selectedProject}
            />
          )}

          {activeSection === "programme" && (
            <div style={{ display: "grid", gap: 16 }}>
              <DueCompletionPanel
                currentProject={currentProject}
                onSave={handleSaveDueCompletion}
              />
              <ProgrammeSection currentProject={currentProject} />
            </div>
          )}
          {activeSection === "boq" && <BOQSection currentProjectId={selectedProject} variations={variations} soqItems={soqItems} />}

          {activeSection === "soqImport" && (
            <SOQImportSection
              currentProjectId={selectedProject}
              currentProject={currentProject}
              onImportComplete={handleSoqImportComplete}
            />
          )}

          {activeSection === "soqRegister" && (
            <SOQRegisterSection
              currentProjectId={selectedProject}
              soqItems={soqItems}
              cpaClasses={cpaClasses}
              variations={variations}
              certificates={certificates}
              handleUpdateSoqItem={handleUpdateSoqItem}
              handleCreateCpaClass={handleCreateCpaClass}
              handleDeleteCpaClass={handleDeleteCpaClass}
            />
          )}

          {activeSection === "cpa" && (
            <CPASection
              currentProjectId={selectedProject}
              contractType={currentCpaType}
              cpaColumns={cpaColumns}
              setCpaColumns={handleSetCpaColumns}
              fidicCpaTables={fidicCpaTables}
              setFidicCpaTables={handleSetFidicCpaTables}
              storedCertificates={certificates}
              availableCertificateMonths={monthRange.map((month) => ({
                value: month,
                label: formatMonthLabel(month),
              }))}
              handleViewItem={handleViewItem}
            />
          )}

          {activeSection === "roe" && (
            <ROEsection
              roeRows={roeRows}
              setRoeRows={handleSetRoeRows}
              certificateOptions={(certificates || []).map((item) => item.certificateNo || item.id).filter(Boolean)}
              selectedCertificateNo={certificateForm.certificateNo}
              selectedClaimMonth={certificateForm.certificateMonth}
            />
          )}

          {activeSection === "financial" && (
            <FinancialDashboardSection
              currentProject={currentProject}
              boqRows={boqRows}
              boqTenderedTotal={boqTenderedTotal}
              boqRevisedTotal={boqRevisedTotal}
              variations={variations}
              claims={claims}
              certificates={certificates}
              claimsDashboard={claimsDashboard}
              totalClaimCost={totalClaimCost}
              soqItems={soqItems}
            />
          )}

          {activeSection === "contract" && (
            <Contractsection
              currentProjectId={selectedProject}
              scheduledWorksTarget={boqTenderedTotal || numberOrZero(currentProject?.scheduledWorks)}
            />
          )}
          {activeSection === "env" && <EnvironmentalAuditSection currentProjectId={selectedProject} />}
          {activeSection === "hs" && <HealthSafetyAuditSection currentProjectId={selectedProject} />}
          {activeSection === "diary" && (
            <DiariesSection
              diaryForm={diaryForm}
              setDiaryForm={setDiaryForm}
              handleAddDiary={handleAddDiary}
              diaries={diaries}
              programmeActivities={programmeActivities}
              progressAssets={progressAssets}
              currentProjectId={selectedProject}
              editingDiaryId={editingDiaryId}
              handleEditDiary={handleEditDiary}
              handleDeleteDiary={handleDeleteDiary}
              clearDiaryForm={clearDiaryForm}
              handleCopyPreviousDiary={handleCopyPreviousDiary}
              handleViewItem={handleViewItem}
            />
          )}

          {activeSection === "visualProgress" && (
            <VisualProgressTracker selectedProject={currentProject || { id: selectedProject }} />
          )}

          {activeSection === "quality" && (
            <QualityTestsSection
              currentProjectId={selectedProject}
              currentUser={currentUser}
            />
          )}
          {activeSection === "ncr" && (
            <NCRSection
              ncrForm={ncrForm}
              setNcrForm={setNcrForm}
              handleAddOrUpdateNcr={handleAddOrUpdateNcr}
              ncrs={ncrs}
              currentProjectId={selectedProject}
              editingNcrId={editingNcrId}
              handleEditNcr={handleEditNcr}
              handleDeleteNcr={handleDeleteNcr}
              clearNcrForm={clearNcrForm}
              handleViewItem={handleViewItem}
            />
          )}

{activeSection === "defects" && (
  <DefectsSection
    currentProject={currentProject}
    currentProjectId={selectedProject}
    currentUser={currentUser}
    progressAssets={progressAssets}
  />
)}

{activeSection === "mos" && (
  <MaterialsOnSiteSection
    currentProject={currentProject}
    currentProjectId={selectedProject}
    currentUser={currentUser}
    soqItems={soqItems}
    certificates={certificatesWithCpa}
  />
)}

{activeSection === "drawings" && (
  <DrawingRegisterSection
    currentProject={currentProject}
    currentProjectId={selectedProject}
  />
)}

{activeSection === "completion" && (
  <CompletionDefectsSection
    currentProject={currentProject}
    currentProjectId={selectedProject}
  />
)}

{activeSection === "timeBars" && (
  <TimeBarSection
    currentProject={currentProject}
    currentProjectId={selectedProject}
  />
)}

{activeSection === "finalAccount" && (
  <FinalAccountSection
    currentProject={currentProject}
    currentProjectId={selectedProject}
  />
)}

{activeSection === "templates" && (
  <TemplatesSection
    currentProject={currentProject}
  />
)}


{activeSection === "meetings" && (
  <ContractMeetingsSection
    currentProject={currentProject}
    currentProjectId={selectedProject}
    currentUser={currentUser}
    certificates={certificatesWithCpa}
    variations={variations}
    claims={claims}
    rfis={rfis}
    instructions={instructions}
    ncrs={ncrs}
    diaries={diaries}
    programmeActivities={programmeActivities}
  />
)}


{activeSection === "risk" && (
  <RiskSection currentProjectId={selectedProject} />
)}

{activeSection === "projectRegister" && userClaims.admin && (
  <ProjectAdminSection
    projects={projects}
    selectedProject={selectedProject}
    onSelect={openProjectFromRegister}
    onAdd={beginAddProject}
    onEdit={editProjectFromRegister}
    onDelete={deleteProject}
  />
)}

{activeSection === "users" && userClaims.admin && (
  <UserManagement currentUser={currentUser} />
)}

{activeSection === "clients" && userClaims.admin && (
  <ClientManagement currentUser={currentUser} />
)}



          {activeSection === "audit" && (
            <AuditTrailSection auditLogs={auditLogs} currentProjectId={selectedProject} />
          )}
        </section>
        <footer style={{ padding: "18px 24px 28px", textAlign: "center", color: "#94a3b8", fontSize: 11, lineHeight: 1.5 }}>
          <div style={{ fontWeight: 600, color: "#64748b" }}>{COPYRIGHT}</div>
          <div style={{ marginTop: 2 }}>{RESTRICTION}</div>
          <div style={{ marginTop: 4, color: "#cbd5e1", fontVariantNumeric: "tabular-nums" }}>
            Build v{BUILD_INFO.version} · {BUILD_INFO.commit} · {BUILD_INFO.date}
          </div>
        </footer>
      </main>

      {viewModal.isOpen && (
        <div style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15, 23, 42, 0.55)",
          display: "grid",
          placeItems: "center",
          padding: 24,
          zIndex: 1000,
        }}>
          <div style={{
            width: "min(720px, 95vw)",
            maxHeight: "85vh",
            overflow: "auto",
            background: "#ffffff",
            borderRadius: 18,
            padding: 20,
            border: "1px solid #dbe7f5",
            boxShadow: "0 25px 80px rgba(15,23,42,0.25)",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "center" }}>
              <h3 style={{ margin: 0 }}>{viewModal.title}</h3>
              <button onClick={closeViewModal} style={{ ...APP_STYLES.navButton, width: "auto", color: "#0f172a" }}>
                Close
              </button>
            </div>
            <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", background: "#f8fafc", padding: 16, borderRadius: 12 }}>
              {JSON.stringify(viewModal.item, null, 2)}
            </pre>
          </div>
        </div>
      )}

    </div>
  );
}

export default App;
